import * as THREE from 'three';
import { Vector3 } from 'three';
import { pipe } from '@vaaas/fpts/function';
import { map, filter } from '@vaaas/fpts/iter';
import { middle, unique } from '@vaaas/fpts/array';
import { sleep } from '@vaaas/fpts/promise';
import App from './application-container';
import { withProgress } from './state/spinner';
import {
    animation_is_suppressed,
    suppress_ui_messages,
    ui_messages_are_suppressed,
    unsuppress_ui_messages_if_ready,
    recalculate_view_dimensions,
    add_small_ball,
    get_camera_look_vector,
    move_camera_by_vector,
    adjust_camera_up_and_update_projection_matrix,
    find_camera_target_point_for_switch_view,
    rotate_camera_by_axis_and_angle_in_steps,
    camera_looking_down_orthogonal_axis,
    show_rotation_center,
    hide_rotation_center,
    adjust_camera_position_along_axis_and_depth,
    update_dom_text_values_and_coordinates,
    raycaster_intersect_objects,
    find_intersection_object,
    suppress_animate,
    unsuppress_animate_if_ready,
    request_setting_button_states,
    must_view_down_negative_direction,
    suppress_undo_tracking,
    find_currently_pointed_object,
    find_all_intersection_objects_relevant_to_face,
    push_nodups,
} from './utility';
import { isWebGLPossible } from '@phi/util/dom';
import {
    LOG,
    clear_all_messages,
    display_message_to_user,
    withSuppressedLogging,
} from '@phi/logging/frontend';
import { clipping_on, toggle_clipping } from '../../../../src/features/clipping';
import { show_modal, hide_modal } from './modals';
import * as v3 from './vector3';
import * as geom from './geometry';
import { rotationMatrixBetweenVectors } from './matrix3';
import { WrappedValue } from '@phi/wrapped-value';
import {
    mustNotDisplayEdge,
    mustNotDisplayFace,
    mustNotDisplayVertex,
    remove_from_symmetry_set,
    selected_objects_are_for_rotating_edge_tangent,
} from './lattice';
import { display_tangencies, hide_tangencies } from './uid_continuity';
import {
    uid_unhighlight_all_objects,
    remove_temporary_highlight_of_faces,
    update_shadow_light_dimensions,
    make_faces_blue,
    set_default_vertex_color,
    uid_unhighlight_object,
} from './colours_materials_appearance';
import {
    display_toggle_face_visibility,
    display_toggle_vertex_visibility,
    uid_calculate_global_bounding_box,
    uid_deselect_all_objects,
    uid_deselect_reference_face,
    uid_hide_rigid_move_manipulator,
    uid_select_all_objects,
    uid_select_object,
    uid_show_rigid_move_manipulator,
    uid_toggle_select_object,
} from './ui_display';
import { loadGivenModelString } from './input_output';
import DefaultModelData from './DefaultModelData';
import { log } from '@phi/frontend/api/calls';
import {
    uil_add_new_vertex,
    uil_get_face_geometry,
    uil_get_point_at_edge_param,
    uil_get_vertices_of_edge,
    uil_get_world_scaling_factor,
    uil_is_joint_smooth,
    uil_is_object_visible,
    uil_is_vertex_of_face,
    uil_move_vertex_to_coords,
    uil_set_display_face_triangulation_res,
} from './ui_logic';
import { plane_ray_intersect, triangle_normal, vertex_distance } from './geometry_and_math';
import {
    handle_mouseup_click_adding_vertex_events,
    is_button_pressed,
} from './pointer/mouse';
import {
	pointer_normalised,
	update_pointer_normalised,
} from './pointer';
import { is_connected_to_onshape, viewsize_to_vertex_diameter_ratio } from './state';

export function init(first_init, passed_tut_stage ) {
    onshape_document_id = App.state.document || '';
    onshape_workspace_id = App.state.workspace || '';
    onshape_element_id = App.state.element || '';
    App.state.tut_stage = passed_tut_stage || 0;

    if (first_init) {
        bind_console_output();
        setup_event_handlers();
    } else if (is_connected_to_onshape()) {
        log('New initialization', "New");
    }

    // Calculate the viewsize and other camera parameters based on a pre-defined global bounding box:
    uid_global_bb = 2;
    viewSize = uid_global_bb * 1.5;
    original_view_size = viewSize;
    viewToWorld = viewSize / App.state.canvas.height;
    uid_ball_size = uid_default_ball_size; // Initially set a default value, as soon as scene is loaded will re-calculate.
    uid_ball_hardness = 0.0;
    curvature_coloring_factor = 1.0;

    // Create renderer (only once)
    if (first_init) {
        if (!isWebGLPossible()) {
            App.state.catastrophic_error = 'Did not detect WebGL support. WebGL is required for Phi to operate. Please consider using a browser with WebGL support. If you believe your browser already supports WebGL, try restarting your browser.';
            return;
        }
        renderer = new THREE.WebGLRenderer({
            antialias: true, // to get smoother output
            alpha: true, // Transparent
            preserveDrawingBuffer: true // to allow screenshot
        });
        renderer.autoClear = false;
        renderer.setClearColor(0x000000, 0.0);

        renderer.setSize(App.state.canvas.width, App.state.canvas.height);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.shadowMap.autoUpdate = false;
        renderer.autoClear = true;
        renderer.domElement.style.zIndex = "-20";
        renderer.localClippingEnabled = false;

        document.getElementById("scene-container").appendChild(renderer.domElement);
        setTimeout(() => recalculate_view_dimensions(false) , 1500);
    }

    // Create scene
    scene = new THREE.Scene();
    //scene.fog = new THREE.Fog(0xefd1b5, 20, 80);

    // Temporary, camera dimensions will be corrected in a moment.
    create_camera_and_camera_controls();
    destroy_axes();
    create_scene_nodes();

    // Initialize the UI display data structures and variables.
    uid_init(first_init);

    // Re-calculate coordinates of screen after a moment, to give HTML time to "settle". Couldn't think of a better way...
    setTimeout(() => recalculate_view_dimensions(false) , 1500);
    set_button_states();
    animate();
}

// animation loop
let animateRequested = false; // Semaphore for avoiding overlapping animation loops.

export function animate() {
    if (animation_is_suppressed())
        return;

    if (animateRequested)
        return;

    if (!uid_camera)
        return;

    animateRequested = true;

    // Check where camera is looking
    var pLocal = new THREE.Vector3(0, 0, -1);
    var pWorld = pLocal.applyMatrix4(uid_camera.matrixWorld);
    viewdir = pWorld.sub(uid_camera.position).normalize();
    viewup = uid_camera.up;
    pLocal = null;

    // Move spotlight
    if (uid_camera && global_spotLight) {
        global_spotLight.position.set(uid_camera.position.x, uid_camera.position.y, uid_camera.position.z);
        global_spotLight.position.addScaledVector(uid_camera.up.normalize(), uid_camera.position.length() / 2.0);
        global_static_vector.crossVectors(uid_camera.up.normalize(), get_camera_look_vector(uid_camera));
        global_spotLight.position.addScaledVector(global_static_vector, uid_camera.position.length() / 2.0);
    }

    render();
}

export function animate_direct() {
    if (animateRequested)
        return;

    animateRequested = true;
    render();
}

let renderRequested = false;

export function render() {
    global_uniforms.u_time.value += global_clock.getDelta();
    renderer.render(scene, uid_camera);
    animateRequested = false;
}

export function add_working_plane(center, normal) {
    var plane_material = new THREE.MeshBasicMaterial({ color: 0xccbbaa });
    plane_material.side = THREE.DoubleSide;
    plane_material.transparent = true;
    plane_material.opacity = 0.2;
    plane_material.depthTest = false;
    plane_material.depthWrite = false;

    var bb = uid_calculate_global_bounding_box(true, false, false);
    var plane_geometry = new THREE.PlaneGeometry(bb.dimension * 4, bb.dimension * 4, 1);
    var nv = new THREE.Vector3(0, 0, 1);
    var R = rotationMatrixBetweenVectors(nv, normal);
    // Rotate geometries.
    var m4 = new THREE.Matrix4();
    m4.set(
        R.elements[0],
        R.elements[3],
        R.elements[6],
        0,
        R.elements[1],
        R.elements[4],
        R.elements[7],
        0,
        R.elements[2],
        R.elements[5],
        R.elements[8],
        0,
        0,
        0,
        0,
        1
    );
    plane_geometry.applyMatrix(m4);
    plane_geometry.translate(center.x, center.y, center.z);

    var pl = new THREE.Mesh(plane_geometry, plane_material);
    pl.name = "working_plane";
    uid_working_plane_raynode.add(pl);
    scene.add(uid_working_plane_raynode);
}

export function hide_halo() {
    if (uid_halo)
        uid_halo.hide();
}

export function show_halo(center, normal, move) {
    //uid_halo = new Halo(center, normal);
    if (uid_halo)
        uid_halo.show(center, normal);
}

export function remove_working_plane() {
    for (var i = uid_working_plane_raynode.children.length - 1; i >= 0; i--) {
        uid_working_plane_raynode.remove(uid_working_plane_raynode.children[i]);
    }
    scene.remove(uid_working_plane_raynode);
}

export function add_grid(grid_normal, make_grid_gray = false) {

    var must_center = $('#gridCenteredAtVertex').is(":checked");

    if (uid_selected_vertices.length == 1 && must_center) {
        var v = uid_selected_vertices.firstValue();
        uid_grid_origin.x = v.object3d.position.x;
        uid_grid_origin.y = v.object3d.position.y;
        uid_grid_origin.z = v.object3d.position.z;
    }
    else {
        uid_grid_origin.x = 0;
        uid_grid_origin.y = 0;
        uid_grid_origin.z = 0;
    }

    // Clear previously existing grid
    for (var i = uid_grid_raynode.children.length - 1; i >= 0; i--) {
        uid_grid_raynode.remove(uid_grid_raynode.children[i]);
    }

    // Create the more reasonable of the 3 grids based on the view direction, unless one is given as input.
    var xaxis = new THREE.Vector3(1, 0, 0);
    var yaxis = new THREE.Vector3(0, 1, 0);
    var zaxis = new THREE.Vector3(0, 0, 1);
    var look_vector = get_camera_look_vector(uid_camera).normalize();
    var dx = Math.abs(look_vector.dot(xaxis));
    var dy = Math.abs(look_vector.dot(yaxis));
    var dz = Math.abs(look_vector.dot(zaxis));

    if (!camera_looking_down_orthogonal_axis(uid_camera))
        make_grid_gray = true;

    if (grid_normal == "x") {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "y", "z");
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "z", "y");
        uid_grid_normal = "x";
    }
    else if (grid_normal == "y") {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "x", "z");
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "z", "x");
        uid_grid_normal = "y";
    }
    else if (grid_normal == "z") {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "x", "y");
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "y", "x");
        uid_grid_normal = "z";
    }
    else if (dx >= dy && dx >= dz) {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "y", "z", make_grid_gray);
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "z", "y", make_grid_gray);
        uid_grid_normal = "x";
    }
    else if (dy >= dx && dy >= dz) {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "x", "z", make_grid_gray);
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "z", "x", make_grid_gray);
        uid_grid_normal = "y";
    }
    else {
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "x", "y", make_grid_gray);
        create_planar_grid(uid_grid_min, uid_grid_max, uid_grid_step, "y", "x", make_grid_gray);
        uid_grid_normal = "z";
    }
}

export function create_planar_grid(min, max, step, o1, o2, make_grid_gray = false) {

    // Transform min, max and step from display units to internal units
    min = get_unitless_value_from_display_unit(min);
    max = get_unitless_value_from_display_unit(max);
    step = get_unitless_value_from_display_unit(step);

    var matR1 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.1 });
    var matG1 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.1 });
    var matB1 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.1 });
    var matGR1 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.1 });
    matR1.color.set(color_grid_red);
    matG1.color.set(color_grid_green);
    matB1.color.set(color_grid_blue);
    matGR1.color.set(color_grid_gray);
    var matR2 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.2 });
    var matG2 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.2 });
    var matB2 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.2 });
    var matGR2 = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1, transparent: true, opacity: 0.2 });
    matR2.color.set(color_grid_red);
    matG2.color.set(color_grid_green);
    matB2.color.set(color_grid_blue);
    matGR2.color.set(color_grid_gray);

    matR1.transparent = true;
    matR1.opacity = 0.6;
    matG1.transparent = true;
    matG1.opacity = 0.6;
    matB1.transparent = true;
    matB1.opacity = 0.6;
    matGR1.transparent = true;
    matGR1.opacity = 0.6;
    matR2.transparent = true;
    matR2.opacity = 0.3;
    matG2.transparent = true;
    matG2.opacity = 0.3;
    matB2.transparent = true;
    matB2.opacity = 0.3;
    matGR2.transparent = true;
    matGR2.opacity = 0.3;

    for (var i = min; i <= max; i += step) {
        var g = new THREE.Geometry();
        //g.dynamic = true;
        g.vertices = new Array();
        for (var j = min; j <= max; j += max - min) {
            var x = 0;
            var y = 0;
            var z = 0;

            if (o1 == "x")
                x = i;
            else if (o1 == "y")
                y = i;
            else if (o1 == "z")
                z = i;
            else
                z = 0;

            if (o2 == "x")
                x = j;
            else if (o2 == "y")
                y = j;
            else if (o2 == "z")
                z = j;
            else
                z = 0;

            var p = new THREE.Vector3(x, y, z);
            p.add(uid_grid_origin);
            g.vertices.push(p);
        }

        // Make lighter the lines that are at decimal places
        var in_units = Number(get_display_unit_from_unitless_value(i).toFixed(4));
        var make_line_heavier = Number.isInteger(in_units);

        if (make_grid_gray) {
            var mesh = new THREE.Line(g, make_line_heavier ? matGR1 : matGR2, THREE.LineStrip);
        }
        else if ((o1 == "x" && o2 == "y") || (o1 == "y" && o2 == "x")) {
            var mesh = new THREE.Line(g, make_line_heavier ? matB1 : matB2, THREE.LineStrip);
        }
        else if ((o1 == "x" && o2 == "z") || (o1 == "z" && o2 == "x")) {
            var mesh = new THREE.Line(g, make_line_heavier ? matG1 : matG2, THREE.LineStrip);
        }
        else {
            var mesh = new THREE.Line(g, make_line_heavier ? matR1 : matR2, THREE.LineStrip);
        }
        uid_grid_raynode.add(mesh);
    }
}

export function set_grid_size_and_adjust_grid(s) {
    set_grid_size(s);
    if (App.state.grid_visible) {
        toggle_grid(null);
        toggle_grid(null);
    }
}

export function set_grid_size(s) {

    setgridbuttonactive(s);

    if (uil_get_world_scaling_factor() <= 0) {
        // No scaling factor, use default grid spacing
        uid_grid_min = -10;
        uid_grid_max = 10;
        uid_grid_step = 0.1;
    }
    else {
        if (s=="A") {
            if (App.state.display_units == "cm")
                uid_grid_step = 0.01;
            else if (App.state.display_units == "m")
                uid_grid_step = 0.01;
            else if (App.state.display_units == "mm")
                uid_grid_step = 0.01;
            else if (App.state.display_units == "ft")
                uid_grid_step = 1/16;
            else if (App.state.display_units == "in")
                uid_grid_step = 1/32;
            else if (App.state.display_units == "yd")
                uid_grid_step = 1/36;
        }
        else if (s=="B") {
            if (App.state.display_units == "cm")
                uid_grid_step = 0.05;
            else if (App.state.display_units == "m")
                uid_grid_step = 0.05;
            else if (App.state.display_units == "mm")
                uid_grid_step = 0.05;
            else if (App.state.display_units == "ft")
                uid_grid_step = 1/12;
            else if (App.state.display_units == "in")
                uid_grid_step = 1/16;
            else if (App.state.display_units == "yd")
                uid_grid_step = 1/16;
        }
        else if (s=="C") {
            if (App.state.display_units == "cm")
                uid_grid_step = 0.1;
            else if (App.state.display_units == "m")
                uid_grid_step = 0.1;
            else if (App.state.display_units == "mm")
                uid_grid_step = 0.1;
            else if (App.state.display_units == "ft")
                uid_grid_step = 1/8;
            else if (App.state.display_units == "in")
                uid_grid_step = 1/8;
            else if (App.state.display_units == "yd")
                uid_grid_step = 1/4;
        }
        else if (s=="D") {
            if (App.state.display_units == "cm")
                uid_grid_step = 0.5;
            else if (App.state.display_units == "m")
                uid_grid_step = 0.5;
            else if (App.state.display_units == "mm")
                uid_grid_step = 0.5;
            else if (App.state.display_units == "ft")
                uid_grid_step = 1/4;
            else if (App.state.display_units == "in")
                uid_grid_step = 1/4;
            else if (App.state.display_units == "yd")
                uid_grid_step = 1/3;
        }
        else if (s=="E") {
            if (App.state.display_units == "cm")
                uid_grid_step = 1;
            else if (App.state.display_units == "m")
                uid_grid_step = 1;
            else if (App.state.display_units == "mm")
                uid_grid_step = 1;
            else if (App.state.display_units == "ft")
                uid_grid_step = 1/2;
            else if (App.state.display_units == "in")
                uid_grid_step = 1/2;
            else if (App.state.display_units == "yd")
                uid_grid_step = 1/2;
        }

        // Now find max and min
        var phi_bbox = uid_calculate_global_bounding_box(true, false, false);
        var d = phi_bbox.dimension * 2;
        var step = get_unitless_value_from_display_unit(uid_grid_step);

        // Limit the number of lines we'll have in the grid to avoid huge ones
        if (d/step > 1000 ) {
            if (s == "A") {
                setTimeout(function(){
                    if (App.state.grid_visible)
                        display_message_to_user(LOG.WARN, "Due to the model dimensions, the current grid would be too dense and might cause delays. We have choosen a larger grid spacing for you.");
                },100);
                    set_grid_size("B");
                return;
            }
            else if (s == "B") {
                if (App.state.grid_visible)
                    display_message_to_user(LOG.WARN, "Due to the model dimensions, the current grid would be too dense and might cause delays. We have choosen a larger grid spacing for you.");
                set_grid_size("C");
                return;
            }
            else if (s == "C") {
                if (App.state.grid_visible)
                    display_message_to_user(LOG.WARN, "Due to the model dimensions, the current grid would be too dense and might cause delays. We have choosen a larger grid spacing for you.");
                set_grid_size("D");
                return;
            }
            else if (s == "D") {
                if (App.state.grid_visible)
                    display_message_to_user(LOG.WARN, "Due to the model dimensions, the current grid would be too dense and might cause delays. We have choosen a larger grid spacing for you.");
                set_grid_size("E");
                return;
            }
            else if (s == "E") {
                clear_all_messages(true);
                if (App.state.grid_visible)
                    setTimeout(function(){
                        display_message_to_user(LOG.WARN, "Due to the model dimensions, the current grid would be too dense and might cause delays. We have reduced the size of the grid to avoid this. Alternatively please choose a larger spacing.");
                    },300);
                d = step * 600;
            }
        }

        var max = Math.max(1.0, Math.ceil(d/step)) * step;
        var min = max * -1;
        uid_grid_max = get_display_unit_from_unitless_value(max);
        uid_grid_min = get_display_unit_from_unitless_value(min);
    }
}

export function setgridbuttonactive(v) {

    ($("#gridsizebtnA").removeClass("active"));
    ($("#gridsizebtnB").removeClass("active"));
    ($("#gridsizebtnC").removeClass("active"));
    ($("#gridsizebtnD").removeClass("active"));
    ($("#gridsizebtnE").removeClass("active"));

    if (v=="A")
        ($("#gridsizebtnA").addClass("active"));
    else if (v=="B")
        ($("#gridsizebtnB").addClass("active"));
    else if (v=="C")
        ($("#gridsizebtnC").addClass("active"));
    else if (v=="D")
        ($("#gridsizebtnD").addClass("active"));
    else if (v=="E")
        ($("#gridsizebtnE").addClass("active"));

    currently_selected_grid_button = v;
}


export function resize_all_vertices_for_zoom(oldviewsize, newviewsize, justscale) {
    // Resize all vertices
    uid_vertex_diameter = newviewsize / viewsize_to_vertex_diameter_ratio.value;
    uid_vertices.values().forEach(function(v) {
        if (!justscale) {
            // Recreate the object
            v.object3d.geometry.dispose();
            v.object3d.geometry = null;
            v.object3d.geometry = new THREE.SphereGeometry(uid_vertex_diameter / vertex_scaling_factor, 8, 6);
            if (uid_selected_vertices.hasItem(v.id)) {
                var scale_factor = 1.1;
                v.object3d.geometry.scale(scale_factor, scale_factor, scale_factor);
            }
        }
        else {
            // Rescale the object
            var scale_factor = newviewsize / oldviewsize;
            if (uid_selected_vertices.hasItem(v.id))
                scale_factor *= 1.1;
            v.object3d.geometry.scale(scale_factor, scale_factor, scale_factor);
        }
        v.object3d.geometry.dynamic = true;
    });

    if (uid_state.showing_reference_stl_vertices || uid_state.showing_reference_partstudio_vertices || justscale)
        resize_all_reference_vertices_for_zoom(oldviewsize, newviewsize, justscale);
}

export function resize_all_reference_vertices_for_zoom( oldviewsize, newviewsize, justscale ) {
    // Resize all vertices
    uid_reference_vertices.values().forEach(function(v) {
        if (v.uid_object.object3d != null) {
                if (!justscale) {
                // Recreate the object
                v.uid_object.object3d.geometry = new THREE.BoxGeometry(
                    uid_vertex_diameter / 2.0,
                    uid_vertex_diameter / 2.0,
                    uid_vertex_diameter / 2.0
                );
            }
            else {
                // Rescale the object
                var scale_factor = newviewsize / oldviewsize;
                v.uid_object.object3d.geometry.scale(
                    scale_factor,
                    scale_factor,
                    scale_factor
                );
            }
            v.uid_object.object3d.geometry.dynamic = true;
        }
    });
}

export function resize_axes_for_zoom() {
    var origin_mesh = uid_axes_raynode.getObjectByName( "axis_origin", true );
    var xaxis_tip_mesh = uid_axes_raynode.getObjectByName( "xaxis_tip", true );
    var yaxis_tip_mesh = uid_axes_raynode.getObjectByName( "yaxis_tip", true );
    var zaxis_tip_mesh = uid_axes_raynode.getObjectByName( "zaxis_tip", true );
    var xy_mesh = uid_axes_planes_raynode.getObjectByName( "xy_axes_plane", true );
    var yz_mesh = uid_axes_planes_raynode.getObjectByName( "yz_axes_plane", true );
    var xz_mesh = uid_axes_planes_raynode.getObjectByName( "xz_axes_plane", true );
    var edgex_mesh = uid_axes_raynode.getObjectByName(    "edgex_mesh", true );
    var edgey_mesh = uid_axes_raynode.getObjectByName(    "edgey_mesh", true );
    var edgez_mesh = uid_axes_raynode.getObjectByName(    "edgez_mesh", true );
    var e1_mesh = uid_axes_raynode.getObjectByName(    "xy_frame_mesh", true );
    var e2_mesh = uid_axes_raynode.getObjectByName(    "yz_frame_mesh", true );
    var e3_mesh = uid_axes_raynode.getObjectByName(    "xz_frame_mesh", true );
    var top_text_mesh = uid_axes_raynode.getObjectByName(    "top_text_mesh", true );
    var front_text_mesh = uid_axes_raynode.getObjectByName(    "front_text_mesh", true );
    var side_text_mesh = uid_axes_raynode.getObjectByName(    "side_text_mesh", true );

    if ( App.state.axes_visible && origin_mesh && xaxis_tip_mesh && yaxis_tip_mesh && zaxis_tip_mesh &&
            xy_mesh && yz_mesh && xz_mesh && edgex_mesh && edgey_mesh && edgez_mesh &&
            e1_mesh && e2_mesh && e3_mesh && top_text_mesh && front_text_mesh && side_text_mesh) {
        dispose_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
            xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
            top_text_mesh, front_text_mesh, side_text_mesh);
        setup_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
            xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
            top_text_mesh, front_text_mesh, side_text_mesh);
    }
    else {
        //console_log_xml("Can't identify coordinate axis meshes");
    }

    animate();
}


export function reattach_rigid_move_manipulator_after_zoom() {
    if (uid_selected_edges.keys().length>0 || uid_selected_faces.keys().length>0 || uid_selected_vertices.keys().length>0)
        uid_show_rigid_move_manipulator(true, 0, true, false, true, selected_objects_are_for_rotating_edge_tangent(), false, false);
    clearTimeout(zoom_manipulator_timeout_handle);
    zoom_manipulator_timeout_handle = null;
}

export function zoom(direction, event, speed_factor = 1.0) {
    hide_halo();

    // If we're dragging don't allow zoom.
    if (App.state.dragging_type == "vertex_normal" ||
            App.state.dragging_type == "edge_list" ||
            App.state.dragging_type == "scale_edge_list" ||
            App.state.dragging_type == "edge_tangent" ||
            App.state.dragging_type == "vertex") {
        display_message_to_user(LOG.WARN, "Can't zoom while dragging.");
        return;
    }

    var current_zoom_speed = App.settings.zoom_speed ? App.settings.zoom_speed : 0.04;
    var deltaZoom = direction * current_zoom_speed * speed_factor;

    update_pointer_normalised(event ? event : { pageX: 0, pageY: 0 });

    var oldViewSize = viewSize;
    zoom_in_or_out(deltaZoom);

    // Pan to keep zoom point unmoved
    if (event) {
        var scalechange = oldViewSize / viewSize;
        var offsetX = pointer_normalised.x - pointer_normalised.x * scalechange;
        var offsetY = pointer_normalised.y - pointer_normalised.y * scalechange;
        var cameraPlaneX = new THREE.Vector3( uid_camera.up.x, uid_camera.up.y, uid_camera.up.z );
        var worlddir = new THREE.Vector3();
        uid_camera.getWorldDirection(worlddir);
        cameraPlaneX.cross(worlddir);
        var cameraPlaneY = uid_camera.up.clone();
        cameraPlaneX.normalize();
        cameraPlaneY.normalize();

        // Must pan camera along by cameraPlaneX * offsetX and cameraplaneY * offsetY;
        var deltaCam = new THREE.Vector3(0, 0, 0);
        deltaCam.addScaledVector( cameraPlaneX, (offsetX * (uid_camera.right - uid_camera.left)) / 2.0 );
        deltaCam.addScaledVector( cameraPlaneY, (-offsetY * (uid_camera.top - uid_camera.bottom)) / 2.0 );

        move_camera_by_vector(uid_camera, deltaCam);
    }

    update_dom_text_values_and_coordinates();
    animate();
}

export function zoom_in_or_out(amount) {
    var dx = uid_camera.right - uid_camera.left;
    var dy = uid_camera.top - uid_camera.bottom;
    uid_camera.left += dx * amount;
    uid_camera.right -= dx * amount;
    uid_camera.top -= dy * amount;
    uid_camera.bottom += dy * amount;

    // The viewSize (initially 10 units in model world space) corresponds to the width (right - left) of the camera in
    // normalized coordinates.
    viewSize *= 1 - 2 * amount;
    viewToWorld = viewSize / App.state.canvas.height;
    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();

    // Adjust line precision accordingly
    //uid_raycaster.linePrecision = viewSize/uid_raycaster_line_precision_factor;
    uid_raycaster.params.Line.threshold = viewSize/uid_raycaster_line_precision_factor;

    //resize_all_vertices_for_zoom(oldViewSize, viewSize, 1);

    // Handle resizing axes and hiding manipulator
    if (App.state.axes_visible) {
        hide_axes();
        must_show_axes_after_zoom = true;
    }

    if (App.state.rigid_move_manipulator) {
        tmp_rigid_move_manipulator_center = new THREE.Vector3();
        tmp_rigid_move_manipulator_axis = new THREE.Vector3();
        tmp_rigid_move_manipulator_normal = new THREE.Vector3();
        tmp_rigid_move_manipulator_center.copy(uid_rigid_move_manipulator.center);
        tmp_rigid_move_manipulator_axis.copy(uid_rigid_move_manipulator.local_x_direction);
        tmp_rigid_move_manipulator_normal.copy(uid_rigid_move_manipulator.local_y_direction);

        uid_hide_rigid_move_manipulator(true, 0, false);
        must_show_manipulator_after_zoom = true;
    }

    if (App.state.tangencies_are_displayed) {
        tangencies_are_temporarily_hidden = true;
        hide_tangencies(true);
    }

        // Hide all vertices
    display_toggle_vertex_visibility(true, true, false);
    must_show_vertices_after_zoom = true;

    if (post_zoom_actions_timeout_handle) {
        clearTimeout(post_zoom_actions_timeout_handle);
        post_zoom_actions_timeout_handle = null;
    }

    post_zoom_actions_timeout_handle = setTimeout(function() { post_zoom_actions();}, 100);
}

export function post_zoom_actions() {
    resize_axes_for_zoom();
    if (must_show_axes_after_zoom)
        display_axes();
    if (must_show_manipulator_after_zoom)
        reattach_rigid_move_manipulator_after_zoom();
    if (must_show_vertices_after_zoom)
        display_toggle_vertex_visibility(true, false, true);
    must_show_axes_after_zoom = false;
    must_show_manipulator_after_zoom = false;
    must_show_vertices_after_zoom = false;

    // Also set camera target on intersection with camera look vector and any object
    var camera_trace = new THREE.Vector2(0,0);
    uid_raycaster.setFromCamera(camera_trace, uid_camera);
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    var intersected_obj = find_intersection_object(intersects);
    if (intersected_obj) {
        // Set target at point of intersection with object
        set_camera_target(uid_camera, intersected_obj.point);
        //add_small_ball(intersected_obj.point.x, intersected_obj.point.y, intersected_obj.point.z, 0, 256, 0, 5);
    }
    else {
        // Set target at point of intersection with plane parallel to screen through object center of mass.
        const bb = uid_calculate_global_bounding_box(true, false, false);
        var c = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
        var p = plane_ray_intersect(c, get_camera_look_vector(uid_camera).normalize(),
                                                                uid_camera.position, get_camera_look_vector(uid_camera).normalize(), 1.0);
        set_camera_target(uid_camera, p);
        //add_small_ball(p.x, p.y, p.z, 0, 256, 0, 5);
    }

    if (App.state.tangencies_are_displayed || tangencies_are_temporarily_hidden) {
        hide_tangencies(true);
        display_tangencies(false, true);
    }
}

export function display_ball(s) {
    var ball_scale = 0.4; // the value we multiply the internal ball size with for display purposes.
    var obj = uid_aux_ball_disk_raynode.children[0];
    uid_aux_ball_disk_raynode.remove(obj);
    var geometry = new THREE.SphereGeometry(s * ball_scale, 32, 32);
    var material = new THREE.MeshLambertMaterial({
        color: 0x959590,
        opacity: 0.4,
        transparent: true,
        depthTest: false,
        depthWrite: false
    });
    material.wireframe = true;
    var circle = new THREE.Mesh(geometry, material);
    circle.position.set(0, 0, 0);
    uid_aux_ball_disk_raynode.add(circle);
    make_all_faces_wireframe();
    animate();
}

export function initialize_axes() {
    if (uid_state.axes_initialized)
        return;

    // Declare meshes
    var origin_mesh = new THREE.Mesh();
    var xaxis_tip_mesh = new THREE.Mesh();
    var yaxis_tip_mesh = new THREE.Mesh();
    var zaxis_tip_mesh = new THREE.Mesh();
    var xy_mesh = new THREE.Mesh();
    var yz_mesh = new THREE.Mesh();
    var xz_mesh = new THREE.Mesh();
    var top_text_mesh = new THREE.Mesh();
    var front_text_mesh = new THREE.Mesh();
    var side_text_mesh = new THREE.Mesh();
    var edgex_mesh = new THREE.Line();
    var edgey_mesh = new THREE.Line();
    var edgez_mesh = new THREE.Line();
    var e1_mesh = new THREE.Line();
    var e2_mesh = new THREE.Line();
    var e3_mesh = new THREE.Line();

    // Set mesh names
    origin_mesh.name="axis_origin";
    xaxis_tip_mesh.name="xaxis_tip";
    yaxis_tip_mesh.name="yaxis_tip";
    zaxis_tip_mesh.name="zaxis_tip";
    xy_mesh.name = "xy_axes_plane";
    yz_mesh.name = "yz_axes_plane";
    xz_mesh.name = "xz_axes_plane";
    edgex_mesh.name = "edgex_mesh";
    edgey_mesh.name = "edgey_mesh";
    edgez_mesh.name = "edgez_mesh";
    e1_mesh.name = "xy_frame_mesh";
    e2_mesh.name = "yz_frame_mesh";
    e3_mesh.name = "xz_frame_mesh";
    top_text_mesh.name = "top_text_mesh";
    front_text_mesh.name = "front_text_mesh";
    side_text_mesh.name = "side_text_mesh";


    // Add axis elements to node (after removing any elements already under node)
    for (var i = uid_axes_raynode.children.length - 1; i >= 0; i--)
        uid_axes_raynode.remove(uid_axes_raynode.children[i]);
    uid_axes_raynode.add(origin_mesh);
    uid_axes_raynode.add(xaxis_tip_mesh);
    uid_axes_raynode.add(yaxis_tip_mesh);
    uid_axes_raynode.add(zaxis_tip_mesh);
    uid_axes_planes_raynode.add(xy_mesh);
    uid_axes_planes_raynode.add(yz_mesh);
    uid_axes_planes_raynode.add(xz_mesh);
    uid_axes_raynode.add(edgex_mesh);
    uid_axes_raynode.add(edgey_mesh);
    uid_axes_raynode.add(edgez_mesh);
    uid_axes_raynode.add(e1_mesh);
    uid_axes_raynode.add(e2_mesh);
    uid_axes_raynode.add(e3_mesh);
    uid_axes_raynode.add(top_text_mesh);
    uid_axes_raynode.add(front_text_mesh);
    uid_axes_raynode.add(side_text_mesh);

    // Set materials
    origin_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.body });
    xaxis_tip_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.x });
    yaxis_tip_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.y });
    zaxis_tip_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.z });
    xy_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.body });
    xy_mesh.material.side = THREE.DoubleSide;
    xy_mesh.material.transparent = true;
    xy_mesh.material.opacity = 0.05;
    xy_mesh.material.depthTest = false;
    xy_mesh.material.depthWrite = false;
    yz_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.body });
    yz_mesh.material.side = THREE.DoubleSide;
    yz_mesh.material.transparent = true;
    yz_mesh.material.opacity = 0.05;
    yz_mesh.material.depthTest = false;
    yz_mesh.material.depthWrite = false;
    xz_mesh.material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.body });
    xz_mesh.material.side = THREE.DoubleSide;
    xz_mesh.material.transparent = true;
    xz_mesh.material.opacity = 0.05;
    xz_mesh.material.depthTest = false;
    xz_mesh.material.depthWrite = false;
    edgex_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.x, linewidth: 2 });
    edgey_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.y, linewidth: 2 });
    edgez_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.z, linewidth: 2 });
    e1_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.edges, linewidth: 2 });
    e2_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.edges, linewidth: 2 });
    e3_mesh.material = new THREE.LineBasicMaterial({ color: App.state.colours.axes.edges, linewidth: 2 });
    var plane_text_material = new THREE.MeshBasicMaterial({ color: App.state.colours.axes.text, transparent: true, opacity: 0.4 });
    top_text_mesh.material = plane_text_material;
    front_text_mesh.material = plane_text_material;
    side_text_mesh.material = plane_text_material;

    // Set geometries (after disposing existing ones)
    dispose_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
        xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
        top_text_mesh, front_text_mesh, side_text_mesh);

    setup_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
        xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
        top_text_mesh, front_text_mesh, side_text_mesh);

    uid_state.axes_initialized = true;
}

export function setup_main_axis_geometries (origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
            xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
            top_text_mesh, front_text_mesh, side_text_mesh) {

    var vx = new THREE.Vector3(1, 0, 0);
    var vy = new THREE.Vector3(0, 1, 0);
    var vz = new THREE.Vector3(0, 0, 1);
    var cyl_radius = viewSize / 400;
    var axis_length = viewSize / 5;
    var plane_size = axis_length * 1.5;

    var origin_geometry = new THREE.SphereGeometry(cyl_radius * 2);
    origin_mesh.geometry = origin_geometry;

    var xaxis_tip_geometry = new THREE.CylinderGeometry(0, cyl_radius * 3, cyl_radius * 7, 32, 1, true);
    var yaxis_tip_geometry = new THREE.CylinderGeometry(0, cyl_radius * 3, cyl_radius * 7, 32, 1, true);
    var zaxis_tip_geometry = new THREE.CylinderGeometry(0, cyl_radius * 3, cyl_radius * 7, 32, 1, true);
    geom.rotate_ip(vx, xaxis_tip_geometry);
    geom.rotate_ip(vy, yaxis_tip_geometry);
    geom.rotate_ip(vz, zaxis_tip_geometry);
    xaxis_tip_geometry.translate(axis_length + cyl_radius * 10, 0, 0);
    yaxis_tip_geometry.translate(0, axis_length + cyl_radius * 10, 0);
    zaxis_tip_geometry.translate(0, 0, axis_length + cyl_radius * 10);
    xaxis_tip_mesh.geometry = xaxis_tip_geometry;
    yaxis_tip_mesh.geometry = yaxis_tip_geometry;
    zaxis_tip_mesh.geometry = zaxis_tip_geometry;

    var xy_geometry = new THREE.PlaneGeometry(plane_size*2.0, plane_size*2.0, 1);
    var yz_geometry = new THREE.PlaneGeometry(plane_size*2.0, plane_size*2.0, 1);
    var xz_geometry = new THREE.PlaneGeometry(plane_size*2.0, plane_size*2.0, 1);
    yz_geometry.rotateY(Math.PI / 2.0);
    xz_geometry.rotateX(Math.PI / 2.0);
    xy_mesh.geometry = xy_geometry;
    yz_mesh.geometry = yz_geometry;
    xz_mesh.geometry = xz_geometry;

    var p0 = new THREE.Vector3(0,0,0);
    var px = new THREE.Vector3(plane_size,0,0);
    var py = new THREE.Vector3(0,plane_size ,0);
    var pz = new THREE.Vector3(0,0,plane_size);
    var edgex_geometry = new THREE.Geometry();
    edgex_geometry.dynamic = true;
    edgex_geometry.vertices = new Array();
    edgex_geometry.vertices.push(p0);
    edgex_geometry.vertices.push(px);
    edgex_mesh.geometry = edgex_geometry;
    var edgey_geometry = new THREE.Geometry();
    edgey_geometry.dynamic = true;
    edgey_geometry.vertices = new Array();
    edgey_geometry.vertices.push(p0);
    edgey_geometry.vertices.push(py);
    edgey_mesh.geometry = edgey_geometry;
    var edgez_geometry = new THREE.Geometry();
    edgez_geometry.dynamic = true;
    edgez_geometry.vertices = new Array();
    edgez_geometry.vertices.push(p0);
    edgez_geometry.vertices.push(pz);
    edgez_mesh.geometry = edgez_geometry;

    var e1_geometry = new THREE.Geometry();
    e1_geometry.dynamic = true;
    e1_geometry.vertices = new Array();
    e1_geometry.vertices.push(new THREE.Vector3(-plane_size, -plane_size, 0));
    e1_geometry.vertices.push(new THREE.Vector3(-plane_size, plane_size, 0));
    e1_geometry.vertices.push(new THREE.Vector3(plane_size, plane_size, 0));
    e1_geometry.vertices.push(new THREE.Vector3(plane_size, -plane_size, 0));
    e1_geometry.vertices.push(new THREE.Vector3(-plane_size, -plane_size, 0));
    e1_mesh.geometry = e1_geometry;

    var e2_geometry = new THREE.Geometry();
    e2_geometry.dynamic = true;
    e2_geometry.vertices = new Array();
    e2_geometry.vertices.push(new THREE.Vector3(0, -plane_size, -plane_size));
    e2_geometry.vertices.push(new THREE.Vector3(0, -plane_size, plane_size));
    e2_geometry.vertices.push(new THREE.Vector3(0, plane_size, plane_size));
    e2_geometry.vertices.push(new THREE.Vector3(0, plane_size, -plane_size));
    e2_geometry.vertices.push(new THREE.Vector3(0, -plane_size, -plane_size));
    e2_mesh.geometry = e2_geometry;

    var e3_geometry = new THREE.Geometry();
    e3_geometry.dynamic = true;
    e3_geometry.vertices = new Array();
    e3_geometry.vertices.push(new THREE.Vector3(-plane_size, 0, -plane_size));
    e3_geometry.vertices.push(new THREE.Vector3(-plane_size, 0, plane_size));
    e3_geometry.vertices.push(new THREE.Vector3(plane_size, 0, plane_size));
    e3_geometry.vertices.push(new THREE.Vector3(plane_size, 0, -plane_size));
    e3_geometry.vertices.push(new THREE.Vector3(-plane_size, 0, -plane_size));
    e3_mesh.geometry = e3_geometry;

    if (!top_text_geometry) {
        top_text_geometry = get_text_geometry("TOP", 1);
        front_text_geometry = get_text_geometry("FRONT", 1);
        side_text_geometry = get_text_geometry("SIDE", 1);
        //top_text_geometry.rotateZ(Math.PI / 2.0);
        side_text_geometry.rotateY(Math.PI / 2.0);
        side_text_geometry.rotateX(Math.PI / 2.0);
        front_text_geometry.rotateX(Math.PI / 2.0);
        //front_text_geometry.rotateZ(Math.PI);
        top_text_mesh.geometry = top_text_geometry;
        front_text_mesh.geometry = front_text_geometry;
        side_text_mesh.geometry = side_text_geometry;
    }
    // Scale it
    var scale_factor = 0.03 * viewSize;
    top_text_mesh.scale.set(scale_factor, scale_factor, scale_factor);
    front_text_mesh.scale.set(scale_factor, scale_factor, scale_factor);
    side_text_mesh.scale.set(scale_factor, scale_factor, scale_factor);

    top_text_mesh.position.set(axis_length * 0.9, axis_length * 1.2, axis_length/25.0);
    side_text_mesh.position.set(axis_length/25.0, axis_length * 0.9, axis_length * 1.2);
    front_text_mesh.position.set(axis_length * 0.7, axis_length/25.0, axis_length * 1.2);
}

export function dispose_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
                xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
                top_text_mesh, front_text_mesh, side_text_mesh) {

    if (origin_mesh && origin_mesh.geometry)
        origin_mesh.geometry.dispose();

    if (xaxis_tip_mesh && xaxis_tip_mesh.geometry)
        xaxis_tip_mesh.geometry.dispose();
    if (yaxis_tip_mesh && yaxis_tip_mesh.geometry)
        yaxis_tip_mesh.geometry.dispose();
    if (zaxis_tip_mesh && zaxis_tip_mesh.geometry)
        zaxis_tip_mesh.geometry.dispose();

    if (xy_mesh && xy_mesh.geometry)
        xy_mesh.geometry.dispose();
    if (xz_mesh && xz_mesh.geometry)
        xz_mesh.geometry.dispose();
    if (yz_mesh && yz_mesh.geometry)
        yz_mesh.geometry.dispose();

    if (edgex_mesh && edgex_mesh.geometry)
        edgex_mesh.geometry.dispose();
    if (edgey_mesh && edgey_mesh.geometry)
        edgey_mesh.geometry.dispose();
    if (edgez_mesh && edgez_mesh.geometry)
        edgez_mesh.geometry.dispose();

    if (e1_mesh && e1_mesh.geometry)
        e1_mesh.geometry.dispose();
    if (e2_mesh && e2_mesh.geometry)
        e2_mesh.geometry.dispose();
    if (e3_mesh && e3_mesh.geometry)
        e3_mesh.geometry.dispose();
}

export function destroy_axes() {
    if (!uid_axes_raynode)
        return;

    var origin_mesh = uid_axes_raynode.getObjectByName( "axis_origin", true );
    var xaxis_tip_mesh = uid_axes_raynode.getObjectByName( "xaxis_tip", true );
    var yaxis_tip_mesh = uid_axes_raynode.getObjectByName( "yaxis_tip", true );
    var zaxis_tip_mesh = uid_axes_raynode.getObjectByName( "zaxis_tip", true );
    var xy_mesh = uid_axes_planes_raynode.getObjectByName( "xy_axes_plane", true );
    var yz_mesh = uid_axes_planes_raynode.getObjectByName( "yz_axes_plane", true );
    var xz_mesh = uid_axes_planes_raynode.getObjectByName( "xz_axes_plane", true );
    var edgex_mesh = uid_axes_raynode.getObjectByName(    "edgex_mesh", true );
    var edgey_mesh = uid_axes_raynode.getObjectByName(    "edgey_mesh", true );
    var edgez_mesh = uid_axes_raynode.getObjectByName(    "edgez_mesh", true );
    var e1_mesh = uid_axes_raynode.getObjectByName(    "xy_frame_mesh", true );
    var e2_mesh = uid_axes_raynode.getObjectByName(    "yz_frame_mesh", true );
    var e3_mesh = uid_axes_raynode.getObjectByName(    "xz_frame_mesh", true );
    var top_text_mesh = uid_axes_raynode.getObjectByName(    "top_text_mesh", true );
    var front_text_mesh = uid_axes_raynode.getObjectByName(    "front_text_mesh", true );
    var side_text_mesh = uid_axes_raynode.getObjectByName(    "side_text_mesh", true );

    for (var i = uid_axes_raynode.children.length - 1; i >= 0; i--)
        uid_axes_raynode.remove(uid_axes_raynode.children[i]);

    if ( App.state.axes_visible && origin_mesh && xaxis_tip_mesh && yaxis_tip_mesh && zaxis_tip_mesh &&
            xy_mesh && yz_mesh && xz_mesh && edgex_mesh && edgey_mesh && edgez_mesh &&
            e1_mesh && e2_mesh && e3_mesh && top_text_mesh && front_text_mesh && side_text_mesh) {
        dispose_main_axis_geometries(origin_mesh, xaxis_tip_mesh, yaxis_tip_mesh, zaxis_tip_mesh,
            xy_mesh, yz_mesh, xz_mesh, edgex_mesh, edgey_mesh, edgez_mesh, e1_mesh, e2_mesh, e3_mesh,
            top_text_mesh, front_text_mesh, side_text_mesh);
    }

    top_text_geometry = null;
    front_text_geometry = null;
    side_text_geometry = null;
}


export function initialize_symmetry_plane() {
    for (var i = uid_symmetry_plane_raynode.children.length - 1; i >= 0; i--) {
        uid_symmetry_plane_raynode.remove(uid_symmetry_plane_raynode.children[i]);
    }

    var vx = new THREE.Vector3(1, 0, 0);
    var vy = new THREE.Vector3(0, 1, 0);
    var vz = new THREE.Vector3(0, 0, 1);

    var plane_material = new THREE.MeshBasicMaterial({ color: 0xaaddcc });
    plane_material.side = THREE.DoubleSide;
    plane_material.transparent = true;
    plane_material.opacity = 0.2;
    plane_material.depthTest = false;
    plane_material.depthWrite = false;


    var bb = uid_calculate_global_bounding_box(true, false, false);
    var plane_geometry = new THREE.PlaneGeometry(bb.dimension * 2, bb.dimension * 2, 1);
    var pl = new THREE.Mesh(plane_geometry, plane_material);

    pl.name = "symmetry_plane";

    uid_symmetry_plane_raynode.add(pl);
    return plane_geometry;
}

export function initialize_mirror_plane() {
    for (var i = uid_mirror_plane_raynode.children.length - 1; i >= 0; i--) {
        uid_mirror_plane_raynode.remove(uid_mirror_plane_raynode.children[i]);
    }

    var vx = new THREE.Vector3(1, 0, 0);
    var vy = new THREE.Vector3(0, 1, 0);
    var vz = new THREE.Vector3(0, 0, 1);

    var plane_material = new THREE.MeshBasicMaterial({ color: 0xddaacc });
    plane_material.side = THREE.DoubleSide;
    plane_material.transparent = true;
    plane_material.opacity = 0.2;
    plane_material.depthTest = false;
    plane_material.depthWrite = false;


    var bb = uid_calculate_global_bounding_box(true, false, false);
    var plane_geometry = new THREE.PlaneGeometry(bb.dimension * 2, bb.dimension * 2, 1);
    var pl = new THREE.Mesh(plane_geometry, plane_material);

    pl.name = "mirror_plane";

    uid_mirror_plane_raynode.add(pl);
    return plane_geometry;
}

export function should_display_mirror_side() {
    return (App.state.mirror_plane_selected);
}

export function refresh_all_faces(only_for_enabling_mirror = false) {
    // Recreate all faces adding the mirror side to each
    // This is a hack to get the new faces to update:
    if (only_for_enabling_mirror)
        uil_set_display_face_triangulation_res(uil_get_display_face_triangulation_res());
    else {
        uid_faces.keys().forEach(function(fid){
            redisplay_face(fid, true);
        });
    }
    animate();
}

export function disable_edit_reference_body_mode() {
    if (App.state.editing_reference_stl) {
        for (const x of Array.from(uid_selected_reference_faces))
            uid_deselect_reference_face(x);
        App.state.editing_reference_stl = false;
        uid_raynode = uid_objects_raynode;
    } else if (App.state.editing_reference_partstudio) {
        for (const x of Array.from(uid_selected_reference_faces))
            uid_deselect_reference_face(x);
        App.state.editing_reference_partstudio = false;
        uid_raynode = uid_objects_raynode;
    }
}

export function edit_reference_partstudio_body() {
    if (App.state.converting_reference_to_phi)
        return;
    if (App.state.editing_reference_stl)
        return;

    suppress_animate();
    deselect_all_reference_faces();
    unsuppress_animate_if_ready();
    if (App.state.editing_reference_partstudio) {
        uid_raynode = uid_objects_raynode;
        App.state.editing_reference_partstudio = false;
    }
    else {
        uid_raynode = uid_ref_partstudio_faces_raynode;
        App.state.editing_reference_partstudio = true;
    }
    request_setting_button_states();
    animate();
}

export function edit_reference_stl_body() {
    if (App.state.converting_reference_to_phi)
        return;
    if (App.state.editing_reference_partstudio)
        return;

    suppress_animate();
    deselect_all_reference_faces();
    unsuppress_animate_if_ready();
    if (App.state.editing_reference_stl) {
        uid_raynode = uid_objects_raynode;
        App.state.editing_reference_stl = false;
    }
    else {
        uid_raynode = uid_ref_stl_faces_raynode;
        App.state.editing_reference_stl = true;
    }
    request_setting_button_states();
    animate();
}

export function toggle_axes() {
    if (App.state.axes_visible)
        hide_axes();
    else {
        resize_axes_for_zoom();
        display_axes();
    }
    animate();
}

export function toggle_locked_rotate() {
    App.state.rotation_locked = !App.state.rotation_locked;
}

export function show_dragged_edge_coordinates(eid) {
    var id_str = "e_" + eid;
    var v_dom_text = new Dom_text("dragged_edge_coordinates", eid, id_str);
    v_dom_text.add_text_to_dom();
}

export function display_axes() {
    if (App.state.axes_visible)
        return;
    scene.add(uid_axes_raynode);
    uid_raynode.add(uid_axes_planes_raynode);
    App.state.axes_visible = true;

    // Finally re-display axes to calculate correct geometry
    resize_axes_for_zoom();
}

export function hide_axes() {
    if (!App.state.axes_visible)
        return;
    scene.remove(uid_axes_raynode);
    // Remove the uid_axes_planes_raynode from any node where it may possibly lie right now
    if (uid_objects_raynode)
        uid_objects_raynode.remove(uid_axes_planes_raynode);
    if (uid_ref_partstudio_faces_raynode)
        uid_ref_partstudio_faces_raynode.remove(uid_axes_planes_raynode);
    if (uid_ref_stl_faces_raynode)
        uid_ref_stl_faces_raynode.remove(uid_axes_planes_raynode);
    if (uid_clipping_planes_raynode)
        uid_clipping_planes_raynode.remove(uid_axes_planes_raynode);
    if (uid_manip_raynode)
        uid_manip_raynode.remove(uid_axes_planes_raynode);
    if (uid_geometries_raynode)
        uid_geometries_raynode.remove(uid_axes_planes_raynode);
    if (uid_axes_planes_raynode.parent)
        uid_axes_planes_raynode.parent.remove(uid_axes_planes_raynode);

    App.state.axes_visible = false;

    // Remove text coordinates from DOM
    dom_text_elements.values().forEach(function(e){
        if(e.text_type == "coordinate_axis") {
            e.remove_text_from_dom();
        }
    });
}

export function redraw_axes() {
    if (App.state.axes_visible) {
        hide_axes();
        requestAnimationFrame(() => display_axes());
    }
}

export function show_reference_partstudio_model() {
    if (App.state.showing_reference_partstudio_models)
        return;

    uid_reference_partstudio_raynode.add(uid_ref_partstudio_faces_raynode);
    uid_reference_partstudio_raynode.add(uid_ref_partstudio_edges_raynode);
    uid_reference_partstudio_raynode.add(uid_ref_partstudio_edges_visible_raynode);

    App.state.showing_reference_partstudio_models = true;
    request_setting_button_states();
    animate();
}

export function hide_reference_partstudio_model() {
    uid_reference_partstudio_raynode.remove(uid_ref_partstudio_faces_raynode);
    uid_reference_partstudio_raynode.remove(uid_ref_partstudio_edges_raynode);
    uid_reference_partstudio_raynode.remove(uid_ref_partstudio_edges_visible_raynode);
    uid_reference_partstudio_raynode.remove(uid_ref_partstudio_vertex_raynode);
    App.state.showing_reference_partstudio_models = false;
    if (App.state.editing_reference_partstudio)
        disable_edit_reference_body_mode();
    request_setting_button_states();
    animate();
}

export function show_reference_stl_model() {
    if (App.state.showing_reference_stl)
        return;
    uid_reference_stl_raynode.add(uid_ref_stl_faces_raynode);
    App.state.showing_reference_stl = true;
    update_shadow_light_dimensions();
    request_setting_button_states();
    animate();
}

export function hide_reference_stl_model() {
    uid_reference_stl_raynode.remove(uid_ref_stl_faces_raynode);
    uid_reference_stl_raynode.remove(uid_ref_stl_vertex_raynode);
    App.state.showing_reference_stl = false;
    request_setting_button_states();
    if (App.state.editing_reference_stl)
        disable_edit_reference_body_mode();
    animate();
}

export function show_reference_parasolid_model() {
    if (App.state.showing_reference_parasolid)
        return;
    uid_reference_parasolid_raynode.add(uid_ref_parasolid_faces_raynode);
    uid_reference_parasolid_raynode.add(uid_ref_parasolid_edges_raynode);
    App.state.showing_reference_parasolid = true;
    update_shadow_light_dimensions();
    request_setting_button_states();
    animate();
}

export function hide_reference_parasolid_model() {
    uid_reference_parasolid_raynode.remove(uid_ref_parasolid_faces_raynode);
    uid_reference_parasolid_raynode.remove(uid_ref_parasolid_edges_raynode);
    App.state.showing_reference_parasolid = false;
    update_shadow_light_dimensions();
    request_setting_button_states();
    animate();
}

export function show_edge_midpoint(eid) {
    var str;

    // If it's not a regular edge (e.g. it's reference edge), abort
    if (!uid_edges.hasItem(eid))
        return;

    // If we're in the process of snapping, don't show midpoint (or equivalent)
    if (App.state.snapping_to_object)
        return;

    // If uid_edge_midpoint_raynode is already in the scene, no need to do anything.
    // Otherwise we must adjust how many children (sections) the node has
    var midpoint_node_found = false;
    scene.children.forEach(function(child) {
        if (child == uid_edge_midpoint_raynode)
            midpoint_node_found = true;
    });

    // If there's only one selected vertex and it's not one of the two endpoints, and there's only one selected edge (or none)
    var snapping_to_vertex = false;
    var snapping_to_vertex_section_points;
    if (uid_selected_edges.length <= 1 && uid_selected_vertices.length == 1 &&
        uil_get_vertices_of_edge(eid).indexOf(uid_selected_vertices.firstKey()) == -1) {
            snapping_to_vertex = true;
            // XXX Here we call the function that will return the parameter value(s) for snapping onto the edge. It may be 0, 1 or 2
            // values. Let's assume 1 (i.e. 2 sections) for now, and that it's 0.333;
            snapping_to_vertex_section_points = find_points_for_snapping_vertex_on_edge_along_xyz(eid, uid_selected_vertices.firstKey(), false);
            uid_edge_midpoint_sections = snapping_to_vertex_section_points.length + 1;
    }

    if (!midpoint_node_found) {
        // Remove all current "midpoint section nodes"
        for (var i = uid_edge_midpoint_raynode.children.length - 1; i >= 0; i--) {
            uid_edge_midpoint_raynode.remove(uid_edge_midpoint_raynode.children[i]);
        }
        // Add new "midpoint section nodes". Variable uid_edge_midpoint_sections tells us how many these are.
        for (var i = 1; i < uid_edge_midpoint_sections; i++) {
            uid_edge_midpoint_raynode.add(uid_edge_midpoint_section_nodes[i-1]);
        }
        scene.add(uid_edge_midpoint_raynode);
    }

    // Calculate positions and size for all nodes
    for (var i = 1; i < uid_edge_midpoint_sections; i++) {
        var section_point;
        if (snapping_to_vertex)
            section_point = snapping_to_vertex_section_points[i-1];
        else
            section_point = 1.0/uid_edge_midpoint_sections * i;

        var v = uil_get_point_at_edge_param(eid, section_point);
        uid_edge_midpoint_section_nodes[i-1].position.set(v.x, v.y, v.z);

        if (snapping_to_vertex && snapping_to_vertex_section_points.length > 0)
            var param = find_edge_snap_to_point_closest_rounded_parameter(eid, snapping_to_vertex_section_points, false);
        else
            var param = find_edge_closest_rounded_parameter(eid, false);

        uid_edge_midpoint_section_nodes[i-1].scale.set(uid_vertex_diameter/3.0, uid_vertex_diameter/3.0, uid_vertex_diameter/3.0);
    }

    uid_edge_midpoint_visible = true;
    animate();
}

export function find_edge_closest_rounded_parameter(eid, is_high_res = false) {
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    var wv_parameter = new WrappedValue();
    var intersection_point = uil_get_ray_edge_intersection(eid, uid_raycaster.ray.origin, uid_raycaster.ray.direction, is_high_res, wv_parameter);
    var section_point, next_section_point;
    var rounded_parameter = 0;

    for (var i = 1; i < uid_edge_midpoint_sections; i++) {
        section_point = (1.0/uid_edge_midpoint_sections * i);
        next_section_point = (1.0/uid_edge_midpoint_sections * (i+1));
        if (rounded_parameter == 0 && wv_parameter.v < (section_point + next_section_point)/2.0)
            rounded_parameter = section_point;
    }
    return (rounded_parameter);
}

export function find_edge_snap_to_point_closest_rounded_parameter(eid, snapping_to_vertex_section_points, is_high_res = false) {
    if (snapping_to_vertex_section_points.length == 1)
        return snapping_to_vertex_section_points[0];
    else if (snapping_to_vertex_section_points.length == 2) {
        uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
        var wv_parameter = new WrappedValue();
        var intersection_point = uil_get_ray_edge_intersection(eid, uid_raycaster.ray.origin, uid_raycaster.ray.direction, is_high_res, wv_parameter);
        if (Math.abs(wv_parameter.v - snapping_to_vertex_section_points[0]) < Math.abs(wv_parameter.v - snapping_to_vertex_section_points[1]))
            return snapping_to_vertex_section_points[0];
        else
            return snapping_to_vertex_section_points[1];
    }
}

export function find_points_for_snapping_vertex_on_edge_along_xyz(eid, vid, do_high_res) {
    var snapping_to_vertex_section_points = [];
    var vs = uil_get_vertices_of_edge(eid);
    var dx = Math.abs(uid_vertices.getItem(vs[0]).object3d.position.x - uid_vertices.getItem(vs[1]).object3d.position.x);
    var dy = Math.abs(uid_vertices.getItem(vs[0]).object3d.position.y - uid_vertices.getItem(vs[1]).object3d.position.y);
    var dz = Math.abs(uid_vertices.getItem(vs[0]).object3d.position.z - uid_vertices.getItem(vs[1]).object3d.position.z);

    var wv_tx = new WrappedValue();
    var wv_ty = new WrappedValue();
    var wv_tz = new WrappedValue();
    var res = uil_get_edge_planes_from_point_intersection(eid, uid_vertices.getItem(vid).object3d.position, do_high_res, wv_tx, wv_ty, wv_tz);

    if (res) {
        if (wv_tx.v > 0.01 && wv_tx.v < 0.99 && wv_ty.v > 0.01 && wv_ty.v < 0.99 && wv_tz.v > 0.01 && wv_tz.v < 0.99) {
            if (dx >= dy && dx >= dz)
                snapping_to_vertex_section_points.push(wv_tx.v)
            else if (dy >= dx && dy >= dz)
                snapping_to_vertex_section_points.push(wv_ty.v)
            else if (dz >= dx && dz >= dy)
                snapping_to_vertex_section_points.push(wv_tz.v)
        }
        else if (wv_tx.v > 0.01 && wv_tx.v < 0.99 && wv_ty.v > 0.01 && wv_ty.v < 0.99) {
            if (dx >= dy)
                snapping_to_vertex_section_points.push(wv_tx.v)
            else if (dy >= dx)
                snapping_to_vertex_section_points.push(wv_ty.v)
        }
        else if (wv_tx.v > 0.01 && wv_tx.v < 0.99 && wv_tz.v > 0.01 && wv_tz.v < 0.99) {
            if (dx >= dz)
                snapping_to_vertex_section_points.push(wv_tx.v)
            else if (dz >= dx)
                snapping_to_vertex_section_points.push(wv_tz.v)
        }
        else if (wv_ty.v > 0.01 && wv_ty.v < 0.99 && wv_tz.v > 0.01 && wv_tz.v < 0.99) {
            if (dy >= dz)
                snapping_to_vertex_section_points.push(wv_ty.v)
            else if (dz >= dy)
                snapping_to_vertex_section_points.push(wv_tz.v)
        }
        else if (wv_tx.v > 0.01 && wv_tx.v < 0.99) {
            snapping_to_vertex_section_points.push(wv_tx.v)
        }
        else if (wv_ty.v > 0.01 && wv_ty.v < 0.99) {
            snapping_to_vertex_section_points.push(wv_ty.v)
        }
        else if (wv_tz.v > 0.01 && wv_tz.v < 0.99) {
            snapping_to_vertex_section_points.push(wv_tz.v)
        }
    }

    return snapping_to_vertex_section_points;
}


export function hide_edge_midpoint(increment_no_of_sections, reset_number_of_sections) {
    scene.remove(uid_edge_midpoint_raynode);
    if (increment_no_of_sections) {
        // Increment the number of sections for next time:
        uid_edge_midpoint_sections += 1;
        if (uid_edge_midpoint_sections == 9)
            uid_edge_midpoint_sections = 2;
    }
    if (reset_number_of_sections)
        uid_edge_midpoint_sections = 2;
    uid_edge_midpoint_visible = false;
    animate();
}


export function show_crosshair(fid, intersected_obj) {
    // If we are hovering over a face with the shift button pressed, in which case we display a line denoting a potential edge
    // Perform crosshair drag
    var edge_ids = [];
    var coords = [];
    var wv_u = new WrappedValue();
    var wv_v = new WrappedValue();
    var intersection_point;
    intersection_point = uil_get_face_ray_intersection(fid, intersected_obj.point, intersected_obj.face.normal.normalize(), wv_u, wv_v, true);
    if (!intersection_point)
        console_log_xml("ERROR in uil_get_face_ray_intersection");
    var res = uil_get_face_crosshair_at_params(fid, wv_u.v, wv_v.v, edge_ids, coords);

    // If we're putting the crosshair on discrete fractions of the edge length, we have to adapt coords accordingly
    if (put_crosshair_at_edge_eighths) {
        for (var i = 0; i < edge_ids.length; i++) {
            var min_point = new THREE.Vector3;
            var min_dist = 100;
            for (var j = 0.0; j <= 1.0; j += crosshair_edge_fraction) {
                var p = uil_get_point_at_edge_param(edge_ids[i], j);
                if (v3.point_point_distance(p, coords[i]) < min_dist ) {
                    min_dist = v3.point_point_distance(p, coords[i]);
                    min_point.copy(p);
                }
            }
            coords[i].copy(min_point);
        }
    }

    if (!res)
        console_log_xml("ERROR in uil_get_face_crosshair_at_params");
    else {
        hide_crosshair();

        var sample_points = [];
        var sample_normals = [];
        var res = calculate_sample_planar_points_for_add_edge(30, coords[0], coords[1], fid, find_face_normal(fid, intersected_obj), sample_points, sample_normals);
        if (res)
            uid_add_points_polyline_to_scene(sample_points, 250, 50, 50, "small");

        var sample_points = [];
        var sample_normals = [];
        res = calculate_sample_planar_points_for_add_edge(30, coords[2], coords[3], fid, find_face_normal(fid, intersected_obj), sample_points, sample_normals);
        if (res)
            uid_add_points_polyline_to_scene(sample_points, 0, 100, 0, "small");
    }

}

export function hide_crosshair() {
    hide_debug_objs(true);
}


export function hide_debug_objs(force = false) {
    if (!force)
        return;

    if (uid_debug_objs_visible) {
        for (var i = uid_debug_obj_raynode.children.length - 1; i >= 0; i--) {
            var obj_to_dispose = uid_debug_obj_raynode.children[i];
            uid_debug_obj_raynode.remove(uid_debug_obj_raynode.children[i]);
            obj_to_dispose.geometry.dispose();
            obj_to_dispose.material.dispose();
            obj_to_dispose = null;
        }
        scene.remove(uid_debug_obj_raynode);
    }
    uid_debug_objs_visible = false;
}

export function toggle_grid(grid_normal, make_gray = false) {
    if (uil_get_world_scaling_factor() == 0) {
        display_message_to_user(LOG.WARN, "Can't add a grid on an empty scene. Please load some model first.");
        return;
    }

    if (!App.state.grid_visible) {
        set_grid_size(currently_selected_grid_button);
        add_grid(grid_normal, make_gray);
        scene.add(uid_grid_raynode);
        App.state.grid_visible = true;
    }
    else {
        scene.remove(uid_grid_raynode);
        App.state.grid_visible = false;
    }
    animate();
}

export function toggle_working_plane() {
    if (!working_plane_set) {
        var center = new THREE.Vector3(0,0,0);
        var normal = new THREE.Vector3(0,0,0);

        if (uid_selected_vertices.length == 0 && uid_selected_edges.length == 0 && uid_selected_faces.length == 1) {
            // Set working plane to selected plane
            var uido = uid_selected_faces.firstValue();
            if (face_is_flat(uido.id)) {
                    var wv_radius = new WrappedValue(0);
                    var wv_flatness = new WrappedValue(0);
                    var characteristic_dir_coords = new THREE.Vector3();
                    uil_get_face_geometry(uido.id, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
            }
            else {
                display_message_to_user(LOG.INFO, "Selected face is not flat. Unable to set working plane.");
                return;
            }
        }
        else if (uid_selected_vertices.length == 3 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0) {
            // Set working plane to include 3 selected vertices
            var v1 = uid_selected_vertices.getItemAtIndex(0);
            var v2 = uid_selected_vertices.getItemAtIndex(1);
            var v3 = uid_selected_vertices.getItemAtIndex(2);
            center.copy(v1.object3d.position);
            normal.copy(triangle_normal(
                v1.object3d.position,
                v2.object3d.position,
                v3.object3d.position
            ));
        }
        else if (uid_selected_vertices.length == 1 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0) {
            // Set working plane to current view plane, with center at the selected vertex
            var v1 = uid_selected_vertices.getItemAtIndex(0);
            center.copy(v1.object3d.position);
            uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
            normal.copy(uid_raycaster.ray.direction);
        }
        else {
            // Set working plane to current view plane, leaving center at 0,0,0
            uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
            normal.copy(uid_raycaster.ray.direction);
        }
        working_plane_set = true;
        working_plane_base.copy(center);
        working_plane_normal.copy(normal)
        add_working_plane(center, normal);
    }
    else {
        working_plane_set = false;
        remove_working_plane();
    }
    animate();
}

export function attempt_to_attach_working_plane_to_face_for_dragging(drag_type, drag_object_id) {
    // According to dragging type (vertex or edge) see if we have a candidate face
    var candidate_drag_faces = [];
    if (drag_type == "vertex") {
        var es = uil_get_edges_of_vertex(drag_object_id);
        es.forEach(function(eid) {
            var fs = uil_get_faces_of_edge(eid);
            if (candidate_drag_faces.indexOf(fs[0]) == -1 && face_is_flat(fs[0], true))
                candidate_drag_faces.push(fs[0]);
            if (candidate_drag_faces.indexOf(fs[1]) == -1 && face_is_flat(fs[1], true))
                candidate_drag_faces.push(fs[1]);
        });
    }
    else if (drag_type == "edge") {
        var fs = uil_get_faces_of_edge(drag_object_id);
        if (candidate_drag_faces.indexOf(fs[0]) == -1 && face_is_flat(fs[0], true))
            candidate_drag_faces.push(fs[0]);
        if (candidate_drag_faces.indexOf(fs[1]) == -1 && face_is_flat(fs[1], true))
            candidate_drag_faces.push(fs[1]);
    }

    var possible_to_attach_working_plane = false;
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    var center = new THREE.Vector3(0,0,0);
    var normal = new THREE.Vector3(0,0,0);
    var min_center = null;
    var min_normal = null;
    var min_angle = Math.PI;
    var look_vector = get_camera_look_vector(uid_camera).normalize();
    var angle_threshold = 0.69; // ~40 degrees in radians

    candidate_drag_faces.forEach(function (fid) {
        uil_get_face_geometry(fid, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
        var a = normal.angleTo(look_vector);
        if (a > Math.PI / 2.0)
            a = Math.PI - a;
        if (a < angle_threshold && a < min_angle) {
            if (!min_center) {
                min_center = new THREE.Vector3();
                min_normal = new THREE.Vector3();
            }
            min_center.copy(center);
            min_normal.copy(normal);
            min_angle = a;
        }
    });

    if (min_center) {
        working_plane_set = true;
        working_plane_base.copy(min_center);
        working_plane_normal.copy(min_normal)
        animate();
    }
}

export function detach_working_plane_for_dragging() {
    working_plane_set = false;
    animate();
}

export function end_display_ball() {
    timeoutvar = setTimeout(function() {
        display_ball_timeout();
    }, 100);
}

export function display_ball_timeout() {
    var obj = uid_aux_ball_disk_raynode.children[0];
    if (obj) {
        uid_aux_ball_disk_raynode.remove(obj);
        make_all_faces_solid();
    }
    animate();
}

export function highlight_objects_while_hovering(pointer_normalised) {
    hide_halo();
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

    /* Nope this is unnecessary and doesn't work
    // Unhighlight any sketches that may be highlighted, if we're in sketch editing mode.
    if (uid_raynode == uid_sketch_plane_raynode)
        unhilight_all_sketches();
    */

    if (intersects.length > 0) {

        // First check whether we are editing the two sketches, which we handle differently to highlight them
        /* All of this is undefined code
        if (uid_raynode == uid_sketch_plane_raynode) {
            var o = find_intersected_sketch(intersects);
            if (o)
                highlight_intersected_sketch(o);
            return;
        }
        */

        var intersected_obj = find_intersection_object(intersects);

        if (intersected_obj == null)
            return;

        if (App.state.drawing_edge) {
                return;
        }
        if (App.state.carving) {
            return;
        }

        if (App.state.offset_face_dragging) {
            // No highlighting
            return;
        }

        // Add halo on object unless we are snapping to reference.
        if (!App.state.snapping_to_object)
            add_halo_to_intersection_point(intersected_obj, pointer_normalised);

        var o3d = uid_raynode.getObjectById(intersected_obj.object.id);

        if (uid_state.brush_grabbing_objects &&
            !is_button_pressed() &&
            !App.state.rigid_move_manipulator &&
            !uid_state.edge_tangent_manipulators_are_shown &&
            !App.state.setting_symmetry_plane &&
            !App.state.setting_mirror_plane &&
            !App.state.selecting_tangent_perpendicular_plane &&
            !App.state.adding_vertex &&
            !uid_state.adding_edge_endpoints_status &&
            !App.state.drawing_edge &&
            !App.state.carving &&
            !uid_state.edge_first_vertex_added &&
            !App.state.offset_face_selecting_vertex &&
            !App.state.offset_face_dragging &&
            !App.state.carving &&
            !App.state.editing_reference_partstudio &&
            !App.state.editing_reference_stl &&
            !uid_state.camera_still_moving &&
            !App.state.panning &&
            !App.state.rotating) {
            if (o3d) {
                var uido = uid_object3dIDToUid_object.getItem(o3d.id);
                if (uido)
                    uid_select_object(uido.id, false, true);
            }
        }
        else {
            if (o3d != uid_highlighted_object_o3d) {
                unhighlight_currently_highlighted_object();
                uid_highlighted_object_o3d = o3d;
                uid_highlight_object(o3d);
                var uido = uid_object3dIDToUid_object.getItem(o3d.id);
                if (uido && uido.type != "edge")
                    hide_edge_midpoint(false, true);
            }
        }

        // Also highlight or unhighlight the symmetry or mirror plane
        if (App.state.mirror_plane_selected) {
            var mesh = uid_mirror_plane_raynode.getObjectByName( "mirror_plane", true );
            if (mesh) {
                if (o3d.name == "mirror_plane")
                    mesh.material.opacity = 0.3;
                else
                    mesh.material.opacity = 0.2;
            }
        }
        if (App.state.symmetry_plane_selected) {
            var mesh = uid_symmetry_plane_raynode.getObjectByName( "symmetry_plane", true );
            if (mesh) {
                if (o3d.name == "symmetry_plane")
                    mesh.material.opacity = 0.3;
                else
                    mesh.material.opacity = 0.2;
            }
        }

    }
    else {
        hide_edge_midpoint(false, true);
        if (App.state.drawing_edge || App.state.carving)
            return;
        unhighlight_currently_highlighted_object();

        // Also see if we should unhighlight the symmetry or mirror plane
        if (App.state.mirror_plane_selected) {
            var mesh = uid_mirror_plane_raynode.getObjectByName( "mirror_plane", true );
            if (mesh)
                mesh.material.opacity = 0.2;
        }
        if (App.state.symmetry_plane_selected) {
            var mesh = uid_symmetry_plane_raynode.getObjectByName( "symmetry_plane", true );
            if (mesh)
                mesh.material.opacity = 0.2;
        }
    }
}

export function clear_symmetry_plane() {
    hide_modal('proceedWithClearSymmetryModal');
    clear_symmetry_set();
    App.state.symmetry_plane_selected = false;
    uid_axes_planes_raynode.remove(uid_symmetry_plane_raynode);
    uid_select_all_objects();
    uid_deselect_all_objects();
    animate();
}

export function set_symmetry_plane() {
    if (clipping_on.value)
        toggle_clipping();

    uid_deselect_all_objects();
    if (App.state.symmetry_plane_selected) {
        show_modal("proceedWithClearSymmetryModal");
    }
    else {
        App.state.setting_symmetry_plane = true;
        display_axes();
    }
    set_button_states();
    animate();
}

export function complete_setting_symmetry_plane() {
    // Add the orthogonal planes node under the objects raynode so it can be ray-casted
    App.state.setting_symmetry_plane = false;

    if (App.state.symmetry_plane_selected) {
        // Bring symmetry plane to correct position/orientation. Use bounding box to center it.
        var bb = uid_calculate_global_bounding_box(true, false, false);
        var o = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
        var v = v3.project_point_on_plane(o, symmetry_plane_base, symmetry_plane_normal);
        var plane_geometry = initialize_symmetry_plane();
        var nv = new THREE.Vector3(0, 0, 1);
        var R = rotationMatrixBetweenVectors(nv, symmetry_plane_normal);
        // Rotate geometries.
        var m4 = new THREE.Matrix4();
        m4.set(R.elements[0], R.elements[3], R.elements[6], 0,
            R.elements[1], R.elements[4], R.elements[7], 0,
            R.elements[2], R.elements[5], R.elements[8], 0,
            0, 0, 0, 1);
            plane_geometry.applyMatrix(m4);
            plane_geometry.translate(v.x, v.y, v.z);

        uid_axes_planes_raynode.add(uid_symmetry_plane_raynode);
    }

    uid_select_all_objects();
    uid_deselect_all_objects();
    uid_unhighlight_all_objects();

    animate();
}


export function set_mirror_plane() {
    if (clipping_on.value)
        toggle_clipping();

    if (App.state.mirror_plane_selected) {
        clear_mirror_plane();
    }
    else {
        uid_deselect_all_objects();
        // Add the orthogonal planes node under the objects raynode so it can be ray-casted
        App.state.setting_mirror_plane = true;
        display_axes();
        set_button_states();
        animate();
    }
}

export function clear_mirror_plane() {
    App.state.mirror_plane_selected = false;
    uid_axes_planes_raynode.remove(uid_mirror_plane_raynode);
    refresh_all_faces(true);

    uid_select_all_objects();
    uid_deselect_all_objects();
    animate();
}


export function complete_setting_mirror_plane() {
    // Add the orthogonal planes node under the objects raynode so it can be ray-casted
    App.state.setting_mirror_plane = false;

    if (App.state.mirror_plane_selected) {
        // Bring mirror plane to correct position/orientation. Use bounding box to center it.
        var bb = uid_calculate_global_bounding_box(true, false, false);
        var o = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
        var v = v3.project_point_on_plane(o, mirror_plane_base, mirror_plane_normal);
        var plane_geometry = initialize_mirror_plane();
        var nv = new THREE.Vector3(0, 0, 1);
        var R = rotationMatrixBetweenVectors(nv, mirror_plane_normal);
        // Rotate geometries.
        var m4 = new THREE.Matrix4();
        m4.set(R.elements[0], R.elements[3], R.elements[6], 0,
            R.elements[1], R.elements[4], R.elements[7], 0,
            R.elements[2], R.elements[5], R.elements[8], 0,
            0, 0, 0, 1);
            plane_geometry.applyMatrix(m4);
            plane_geometry.translate(v.x, v.y, v.z);

            uid_axes_planes_raynode.add(uid_mirror_plane_raynode);
    }

    uid_select_all_objects();
    uid_deselect_all_objects();
    uid_unhighlight_all_objects();

    refresh_all_faces(true);

    animate();
}

export function deghostify_selected_bodies() {
    suppress_undo_tracking();
    suppress_animate();

    var bids = [];
    uid_selected_faces.keys().forEach(function (fid) {
        var bid = uil_get_body_of_face(fid);
        push_nodups(bid, bids);
    });

    bids.forEach(function(bid) {

        // Deselect
        uid_deselect_all_objects();

        // Copy
        var copied_body_ids = uil_copy_bodies([bid], true);
        var copied_body_id = copied_body_ids[0];

        // Collect copied objects
        var es = [];
        var edges_to_flip = [];
        uid_faces.keys().forEach(function(fid){
            if (uil_get_body_of_face(fid) == copied_body_id) {
                es = uil_get_edges_of_face(fid);
                es.forEach(function(eid){
                    push_nodups(eid, edges_to_flip);
                });
            }
        });

        // Scale them to flip to the other side
        var tmp_center = new THREE.Vector3();
        var wv_radius = new WrappedValue(0);
        var characteristic_dir_coords = new THREE.Vector3();
        var tmp_normal = new THREE.Vector3();
        uil_get_edge_list_geometry(edges_to_flip, true, tmp_center, tmp_normal, wv_radius, characteristic_dir_coords);

        var scale_axis = new THREE.Vector3();
        scale_axis.set(mirror_plane_normal.x, mirror_plane_normal.y, mirror_plane_normal.z);

        // Scale around the point of projection of the center of the body onto the symmetry plane
        var mirr_base = new THREE.Vector3(mirror_plane_base.x, mirror_plane_base.y, mirror_plane_base.z);
        var mirr_norm = new THREE.Vector3(mirror_plane_normal.x, mirror_plane_normal.y, mirror_plane_normal.z).normalize();
        var projected_center = v3.project_point_on_plane(tmp_center, mirr_base, mirr_norm);

        uil_start_scale_edge_list(edges_to_flip);
        uil_scale_edge_list(edges_to_flip, projected_center, -1, true, scale_axis)
        uil_end_scale_edge_list(edges_to_flip);

        // Flip inside out
        uil_flip_body_inside_out(copied_body_id);

        // Remove mirror
        set_mirror_plane();
    });

    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
}


export function clear_symmetry_set() {
    symmetry_edges_list_first = [];
    symmetry_edges_list_second = [];
    uid_select_all_objects();
    uid_deselect_all_objects();
    animate();
}

export function find_edge_midpoint(eid) {
    return uil_get_point_at_edge_param(eid, 0.5);
}


export function add_vertex_on_one_or_more_selected_edges_with_shift_pressed() {

    var uido = find_currently_pointed_object();
    if (!uido || uido.type != "edge")
        return;
    uid_select_object(uido.id);

    var edges_to_apply_to = [];
    uid_selected_edges.keys().forEach(function(eid){
        push_nodups(eid, edges_to_apply_to);
    });
    uid_selected_faces.keys().forEach(function(fid){
        uil_get_edges_of_face(fid).forEach(function(eid){
            push_nodups(eid, edges_to_apply_to);
        });
    });

    if (edges_to_apply_to.length == 1 && uid_selected_vertices.length == 1 &&
            uil_get_vertices_of_edge(uido.id).indexOf(uid_selected_vertices.firstKey()) == -1) {
        // We are snapping a vertex onto the edge.

        // XXX Here we call the function that will return the parameter value(s) for snapping onto the edge. It may be 0, 1 or 2
        // values. Let's assume 1 (i.e. 2 sections) for now, and that it's 0.333;
        var snapping_to_vertex_section_points = find_points_for_snapping_vertex_on_edge_along_xyz(uido.id, uid_selected_vertices.firstKey(), true);
        uid_deselect_all_objects();
        if (snapping_to_vertex_section_points.length > 0) {
            var param = find_edge_snap_to_point_closest_rounded_parameter(uido.id, snapping_to_vertex_section_points, true);
            var new_vertex_id = uil_add_vertex_at_param( uido.id, param );
        }
    }
    else {
        // Careful here. If we're adding vertices to at least one edge that is in the symmetry sets, we can't do a compound operation
        // since this will not allow us to add the vertex on the opposite side as well.
        const adding_on_symmetry = edges_to_apply_to.some(eid => (symmetry_edges_list_first.includes(eid) || symmetry_edges_list_second.includes(eid)));

        uid_deselect_all_objects();
        suppress_animate();
        suppress_undo_tracking();
        if (!adding_on_symmetry)
            uil_start_compound_operation();

        edges_to_apply_to.forEach(function(eid){
            add_vertices_at_subsections_of_edge(eid, uid_edge_midpoint_sections - 1);
        });

        if (!adding_on_symmetry)
            uil_end_compound_operation();
        unsuppress_undo_tracking_if_ready(false);
        unsuppress_animate_if_ready();
    }
}

export function add_vertices_at_subsections_of_edge(edge_id, n_vertices) {
    if (n_vertices < 1 || n_vertices > 7)
        return;

    var new_edge_id = edge_id;
    var n = n_vertices;
    while (n >= 1) {
        var param = 1.0/(n+1.0);
        var new_vertex_id = uil_add_vertex_at_param( new_edge_id, param );
        var es = uil_get_edges_of_vertex(new_vertex_id);
        var l1 = uil_get_edge_length(es[0]);
        var l2 = uil_get_edge_length(es[1]);
        new_edge_id = l1 > l2 ? es[0] : es[1];
        n--;
    }
}


export function add_vertex_on_one_or_more_selected_edges_without_shift_pressed() {

    var uido = find_currently_pointed_object();
    if (!uido || uido.type != "edge")
        return;
    uid_select_object(uido.id);

    var edges_to_apply_to = [];
    edges_to_apply_to.push(uido.id);
    uid_selected_edges.keys().forEach(function(eid){
        push_nodups(eid, edges_to_apply_to);
    });
    uid_selected_faces.keys().forEach( function (fid) {
        var es = uil_get_edges_of_face(fid);
        es.forEach(function(eid){
            push_nodups(eid, edges_to_apply_to);
        });
    });

    uid_deselect_all_objects();
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
    var wv_parameter = new WrappedValue();
    var intersection_point = uil_get_ray_edge_intersection(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction, true, wv_parameter);

    suppress_animate();
    suppress_undo_tracking();

    var is_first_edge = true;
    var keep_going = true;
    var first_vertex_point = new THREE.Vector3();
    edges_to_apply_to.forEach(function(eid) {
        if (is_first_edge) {
            var new_vertex_id = uil_add_vertex_at_param( eid, wv_parameter.v );
            if (new_vertex_id)
                first_vertex_point.copy(uid_vertices.getItem(new_vertex_id).object3d.position);
            else
                keep_going = false;
            is_first_edge = false;
        }
        else if (keep_going) {
            var p1, p2 = new THREE.Vector3();
            p1 = uil_get_point_at_edge_param(eid, wv_parameter.v);
            p2 = uil_get_point_at_edge_param(eid, 1.0 - wv_parameter.v);
            if (v3.point_point_distance(p1, first_vertex_point) < v3.point_point_distance(p2, first_vertex_point))
                var new_vertex_id = uil_add_vertex_at_param( eid, wv_parameter.v );
            else
                var new_vertex_id = uil_add_vertex_at_param( eid, 1.0 - wv_parameter.v );
        }
    });

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
}


export function add_edge_between_two_vertices_on_face(v0_id, v1_id, f_id, intermediate_point, force_on_invisible) {
    if (!uil_is_object_visible(f_id) && !force_on_invisible) {
        display_message_to_user(LOG.WARN, "Cannot add edge on invisible face.");
        return 0;
    }

    // Make sure there's no edge there already
    let edge_exists = false;
    uid_edges.keys().forEach(function(eid){
        let vs = uil_get_vertices_of_edge(eid);
        if ((vs[0] == v0_id && vs[1] == v1_id) || (vs[1] == v0_id && vs[0] == v1_id)) {
            edge_exists = true;
        }
    });
    if (edge_exists) {
        display_message_to_user(LOG.WARN, "Edge already exists.");
        return 0;
    }

    // Check that the two vertices don't coincide, as we can't add 0-distance edges
    let eps = 1e-5;
    if (vertex_distance(v0_id, v1_id) < eps) {
        display_message_to_user(LOG.WARN, "Can't add edge between coinciding vertices.");
        return 0;
    }

    // First we make the specific face visible
    var original_face_was_not_visible = false;
    if (mustNotDisplayFace(f_id)) {
        var f = uid_faces.getItem(f_id);
        uid_objects_raynode.add(f.object3d);
        if (singled_out_faces_to_hide.indexOf(f_id) != -1)
            singled_out_faces_to_hide.splice(singled_out_faces_to_hide.indexOf(f_id), 1);
        f.object3d.geometry.verticesNeedUpdate = true;
        f.object3d.geometry.normalsNeedUpdate = true;
        f.object3d.geometry.computeFaceNormals();
        f.object3d.geometry.computeVertexNormals();
        f.object3d.geometry.computeBoundingSphere();
        original_face_was_not_visible = true;
    }

    // We need to calculate a set of points to use for adding the edge
    var linedir = new THREE.Vector3();
    var lineok = uil_calc_ray_dir_for_face_sampling(f_id, v0_id, v1_id, linedir);
    if (lineok) {
        var m = 30;
        if (intermediate_point == null) {
            var sample_points = [];
            var sample_normals = [];
            var res = calculate_sample_planar_points_for_add_edge(m, uid_vertices.getItem(v0_id).object3d.position, uid_vertices.getItem(v1_id).object3d.position,
            f_id, linedir, sample_points, sample_normals);
            if (!res) {
                var sample_points = [];
                var sample_normals = [];
            }
        }
        else {
            var sample_points1 = [];
            var sample_normals1 = [];
            var sample_points2 = [];
            var sample_normals2 = [];
            var res = calculate_sample_planar_points_for_add_edge(m, uid_vertices.getItem(v0_id).object3d.position, intermediate_point, f_id, linedir, sample_points1, sample_normals1);
            if (!res) {
                var sample_points1 = [];
                var sample_normals1 = [];
            }
            res = calculate_sample_planar_points_for_add_edge(m, intermediate_point, uid_vertices.getItem(v1_id).object3d.position, f_id, linedir, sample_points2, sample_normals2);
            if (!res) {
                var sample_points2 = [];
                var sample_normals2 = [];
            }
            sample_points1.pop(); // Remove last element
            sample_points2.splice(0,1); // Remove first element
            sample_normals1.pop(); // Remove last element
            sample_normals2.splice(0,1); // Remove first element
            var sample_points = sample_points1.concat(sample_points2);
            var sample_normals = sample_normals1.concat(sample_normals2);
        }

        //console_log(sample_points);
        var new_edge_id = uil_add_new_edge(f_id, v0_id, v1_id, sample_points, sample_normals, true);
    }
    else {
        var sample_points = [];
        var new_edge_id = uil_add_new_edge(f_id, v0_id, v1_id, [], [], true);
    }

    if ( original_face_was_not_visible && new_edge_id > 0) {
        // Make the two faces on either side of edge visible
        var fs = uil_get_faces_of_edge(new_edge_id);
        if (uil_is_object_visible(fs[0]))
            push_nodups(fs[0], singled_out_faces_to_hide);
        if (uil_is_object_visible(fs[1]))
            push_nodups(fs[1], singled_out_faces_to_hide);

        // Flip twice the isolated faces, just to display the above.
        if (App.state.currently_hiding_faces) {
            display_toggle_face_visibility(false, true)
        }
    }
    else if (original_face_was_not_visible && new_edge_id == 0) {
        // Adding the edge failed, so simply make the original face invisible again
        var f = uid_faces.getItem(f_id);
        uid_objects_raynode.remove(f.object3d);
        push_nodups(f_id, singled_out_faces_to_hide);
    }
    return new_edge_id;
}

export function add_edge_along_points(v0_id, v1_id, f_id, sample_points, sample_normals, make_linear_if_no_samples, make_edge_linear) {

    /*
    for (var i =0; i< sample_points.length;i++){
        var o = sample_points[i];
        add_small_ball(o.x, o.y, o.z, 0, 255, 0); // XXX
        var n = sample_normals[i];
        uid_add_vector_to_scene(o.x, o.y, o.z, n.x, n.y, n.z, 1.0, 0,255,0);
    }
    */

    var new_edge_id = uil_add_new_edge(f_id, v0_id, v1_id, sample_points, sample_normals, false);
    if (make_edge_linear || (new_edge_id && make_linear_if_no_samples && sample_points.length == 0))
        uil_set_linearity_of_edges([new_edge_id], true);
    else if (new_edge_id)
        uil_smoothen_edges([new_edge_id], SOFT_STRENGTH); // XXXXX

    return new_edge_id;
}

export function add_vertex_location_while_drawing_edge_on_face() {
    var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
    if (intersects.length > 0) {
        var intersected_objects;
        // If carving we examine all intersections, otherwise just the first hit.
        if (App.state.carving)
            intersected_objects = find_all_intersection_objects_relevant_to_face(intersects, null);
        else {
            var intersected_obj = find_intersection_object(intersects);
            var intersected_objects = [];
            if (intersected_obj)
                intersected_objects.push(intersected_obj);
        }

        var intersected_face = false;
        intersected_objects.forEach(function(o) {
            var obj = uid_object3dIDToUid_object.getItem(o.object.id);
            var intersected_obj_id = 0;
            var intersected_obj_type = "";
            if (obj) {
                intersected_obj_id = uid_object3dIDToUid_object.getItem(o.object.id).id;
                intersected_obj_type = uid_object3dIDToUid_object.getItem(o.object.id).type;
                if (intersected_obj_type == "face") {
                    add_small_ball(o.point.x, o.point.y, o.point.z, 0, 256, 0, 2);
                    if (!intersected_face) {
                        // Add ray to the list, but just once
                        var current_raycaster_origin = new THREE.Vector3();
                        current_raycaster_origin.copy(o.point);
                        addEdge_draw_raycasting_new_vertex_origins.push(current_raycaster_origin);
                    }
                    intersected_face = true;
                }
            }
        });
    }
}

export function start_adding_vertex(modifier = false) {
    if (App.state.rigid_move_manipulator ||
        App.state.setting_symmetry_plane ||
        App.state.setting_mirror_plane ||
        App.state.selecting_tangent_perpendicular_plane ||
        uid_state.adding_edge_endpoints_status ||
        App.state.drawing_edge ||
        App.state.carving ||
        uid_state.edge_first_vertex_added ||
        App.state.offset_face_selecting_vertex ||
        App.state.offset_face_dragging ||
        App.state.carving ||
        App.state.currently_loading_model)
         return; // Can't do this now.

    var uido = find_currently_pointed_object();
    if (uido && uido.type == "edge") {
        // User clicked "V" while hovering over an edge. So we can proceed to add vertex or vertices, depending.
        uid_select_object(uido.id);

        // If one or more edges are selected and shift is pressed, add vertex to the middle of all selected edges
        if ((uid_selected_edges.keys().length >= 1 || uid_selected_faces.length == 1) && modifier) {
            add_vertex_on_one_or_more_selected_edges_with_shift_pressed();
            hide_edge_midpoint(true, true);
            unsuppress_animate_if_ready();
        }
        else if ((uid_selected_edges.keys().length >= 1 || uid_selected_faces.length == 1) && !modifier) {
            add_vertex_on_one_or_more_selected_edges_without_shift_pressed();
            unsuppress_animate_if_ready();
        }
        else {
            // Handle simple click on an edge.
            handle_mouseup_click_adding_vertex_events();
        }
    }
    else {
        // User clicked on Add Vertex icon or hit V while not over an edge. So we just set the state.
        App.state.adding_vertex = true;
    }

    set_button_states();
}

export function stop_adding_vertex() {
    if (uid_state.adding_edge_endpoints_status)
        return; // Can't do this while adding edge.
    App.state.adding_vertex = false;
    set_button_states();
}

export function add_edge_action(straighten = false) {
    unhighlight_currently_highlighted_object();

    if (App.state.adding_vertex)
        return;

    if (uid_selected_vertices.length >= 2 && uid_selected_edges.length == 0 && uid_selected_faces.length <= 1) {
        suppress_animate();
        suppress_undo_tracking();

        // If more than 2 vertices are selected we'll attempt to add multiple faces. Some may fail in incomprehensible
        // ways, so we'll suppress UI messages in order not to confuse the user.
        var suppressing_ui_messages = false;
        if (uid_selected_vertices.length > 2 && !ui_messages_are_suppressed()) {
            suppressing_ui_messages = true;
            suppress_ui_messages();
        }

        // If 2 vertices were selected, check out if a face was also passed.
        var addEdge_face_object_id = 0;
        if (uid_selected_vertices.length == 2)
            addEdge_face_object_id = uid_selected_faces.length == 1 ? uid_selected_faces.firstKey() : 0;

        // We now allow adding multiple edges between a set of selected vertices all at once. We have to analyse the set of selected
        // vertices and creates pairs that lie on the same face, between which edges will be added.
        // NOTE: We will assume that the user will be passing these vertices in order.
        var vertex_pairs = [];
        for (var i = 0; i < uid_selected_vertices.length - 1; i++) {
            vertex_pairs.push([uid_selected_vertices.getKeyAtIndex(i), uid_selected_vertices.getKeyAtIndex(i+1)]);
        }
        // Check whether first and last vertices are on same face, in which case add one more pair connecting them
        if (uid_selected_vertices.length > 2) {
            var faces_for_v0 = faces_of_vertex(uid_selected_vertices.getKeyAtIndex(0));
            var faces_for_vN = faces_of_vertex(uid_selected_vertices.getKeyAtIndex(uid_selected_vertices.length - 1));
            var filtered_faces = faces_for_v0.filter(function(n) { return faces_for_vN.indexOf(n) !== -1; });
            if (filtered_faces.length > 0) {
                vertex_pairs.push([uid_selected_vertices.getKeyAtIndex(uid_selected_vertices.length - 1), uid_selected_vertices.getKeyAtIndex(0)]);
            }
        }
        uid_deselect_all_objects();

        var edges_added = 0;
        vertex_pairs.forEach(function(vpair) {
            var new_edge_ids = add_edge_between_vertices(vpair[0], vpair[1], addEdge_face_object_id);
            if (new_edge_ids.length > 0 && straighten)
                uil_set_linearity_of_edges(new_edge_ids, true);
            edges_added += new_edge_ids.length;
        });

        if (suppressing_ui_messages) {
            unsuppress_ui_messages_if_ready();
            if (edges_added > 0)
                display_message_to_user(LOG.INFO, edges_added + " edges were added");
            else
                display_message_to_user(LOG.WARN, "No edges were added. Perhaps the vertices were not selected in the correct order?");
        }

        unsuppress_animate_if_ready();
        unsuppress_undo_tracking_if_ready(false);
    }
    else {
        start_drawing_edge_on_face(false, straighten);
    }
}

export function add_edge_between_vertices(addEdge_first_vertex_object_id, addEdge_second_vertex_edge_object_id, addEdge_face_object_id, force_on_invisible = false) {
    // If we have a selected face, then just go ahead and add the edge. Otherwise check which face the two vertices belong to.
    // If more than one is found, then abort.
    let new_edge_ids = [];
    if (addEdge_face_object_id == 0) {
        var found_face_for_pair_of_edges = false;
        const visible_faces = uid_faces.keys().filter(uil_is_object_visible);
        for (const fid of visible_faces) {
            if (uil_is_vertex_of_face(addEdge_first_vertex_object_id, fid) && uil_is_vertex_of_face(addEdge_second_vertex_edge_object_id, fid)) {
                if (uil_is_object_fixed(fid)) {
                    display_message_to_user(LOG.WARN, "Cannot add edge on fixed face.");
                    new_edge_ids = [];
                }
                else {
                    const neweid = add_edge_between_two_vertices_on_face(
                        addEdge_first_vertex_object_id,
                        addEdge_second_vertex_edge_object_id,
                        fid,
                        null,
                        force_on_invisible,
                    );
                    if (neweid > 0)
                        new_edge_ids.push(neweid);
                    found_face_for_pair_of_edges = true;
                }
            }
        }
        if (!found_face_for_pair_of_edges) {
            // Attempt to add edge between vertices not belonging to the same face.
            display_message_to_user(LOG.WARN, "Can only add edge between vertices that are on the boundary of the same visible face.");
            new_edge_ids = [];
        }
    }
    else {
        if (uil_is_object_fixed(addEdge_face_object_id)) {
            display_message_to_user(LOG.WARN, "Cannot add edge on fixed face.");
            new_edge_ids = [];
        }
        else {
            var neweid = add_edge_between_two_vertices_on_face(addEdge_first_vertex_object_id, addEdge_second_vertex_edge_object_id, addEdge_face_object_id, null, force_on_invisible);
            if (neweid > 0)
                new_edge_ids.push(neweid);
            else {
                // If this failed, it's likely because two edges on the vertices being connected are tangent between them.
                // Remove any tangencies and try again.
                var es1 = uil_get_edges_of_vertex(addEdge_first_vertex_object_id);
                var es2 = uil_get_edges_of_vertex(addEdge_second_vertex_edge_object_id);
                es1.forEach(function(e1id){
                    es2.forEach(function(e2id){
                        if (uil_is_joint_smooth(e1id, e2id) == 1)
                            uil_remove_joint_smoothness([[e1id, e2id]], true);
                    });
                });
                var neweid = add_edge_between_two_vertices_on_face(addEdge_first_vertex_object_id, addEdge_second_vertex_edge_object_id, addEdge_face_object_id, null, force_on_invisible);
                if (neweid > 0)
                    new_edge_ids.push(neweid);
            }
        }
    }
    return new_edge_ids;
}

export function start_drawing_edge_on_face(is_carving, modifier = false) {
    unhighlight_currently_highlighted_object();

    if (App.state.adding_vertex)
        return; // Can't do this while adding vertex.

    if (uid_state.adding_edge_endpoints_status)
        return; // Can't do this while adding edge endpoints.

    if (App.state.drawing_edge)
        return; // Was already drawing edge

    if (App.state.carving)
        return; // Was already carving

    // Check that faces and edges are visible.
    if (App.state.currently_hiding_faces) {
        var all_faces_hidden = true;
        uid_faces.keys().forEach(function(fid){
            if (!mustNotDisplayFace(fid))
                all_faces_hidden = false;
        });
    }

    if (App.state.currently_hiding_edges) {
        var all_edges_hidden = true;
        uid_edges.keys().forEach(function(eid){
            if (!mustNotDisplayEdge(eid))
                all_edges_hidden = false;
        });
    }

    if (all_edges_hidden || all_faces_hidden) {
        display_message_to_user(LOG.WARN, "You need to have visible faces and edges in order to draw or carve.");
        return;
    }

    // Suppress mirror side
    if (App.state.mirror_plane_selected) {
        temporarily_hiding_mirror_side = true;
        temporary_mirror_plane_base = new THREE.Vector3();
        temporary_mirror_plane_base.copy(mirror_plane_base);
        temporary_mirror_plane_normal = new THREE.Vector3();
        temporary_mirror_plane_normal.copy(mirror_plane_normal);
        clear_mirror_plane();
    }

    // Will start adding edge
    suppress_undo_tracking();

    uid_deselect_all_objects();

    // Make a copy of this flag to make sure it's still available when processing later on.
    shift_was_pressed_during_adding_edge = modifier;

    hidden_edges_were_visible_before_drawing_edge = App.state.hidden_edges_visible;
    App.state.hidden_edges_visible = false;

    // Set state and empty array of points, relevant variables and flags
    if (!is_carving)
        App.state.drawing_edge = true;
    else
        App.state.carving = true;

    addEdge_draw_raycasting_origins = [];
    addEdge_draw_intersected_faces = [];
    addEdge_draw_intersected_vertices = [];
    addEdge_draw_intersected_edges = [];

    // Increase precision of raycaster
    uid_raycaster.params.Line.threshold /= 2;

    App.state.carving = is_carving;

    App.state.allow_rotate = false;

    // We need a small delay here otherwise the hidden_edges_visible = false triggers a redraw that happens after the blue
    // and we lose the color.
    setTimeout(function() {make_faces_blue();}, 200);

    // Empty lists
    addEdge_draw_raycasting_origins = [];
    addEdge_draw_intersected_faces = [];
    addEdge_draw_raycasting_new_vertex_origins = [];
    request_setting_button_states();
}

export async function stop_drawing_edge_on_face() {
    // suppress_animate();
    // suppress_undo_tracking();

    if (App.state.adding_vertex)
        wrap_up_drawing_edge_on_face(); // Can't do this while adding vertex.

    if (!(App.state.drawing_edge || App.state.carving))
        wrap_up_drawing_edge_on_face();

    // Reset the state
    App.state.drawing_edge = false;
    App.state.carving = false;
    App.state.allow_rotate = true;

    // Remove points from scene
    clear_small_balls();
    uid_deselect_all_objects();

    // Decrease precision of raycaster
    uid_raycaster.params.Line.threshold *= 2;

    // We now examine all of the intersected objects we have, to build a list of "hot" vertices which will be connected by new edges.
    // Every edge intersection will contrinute one hot vertex. But before we look at those, we'll consider any pre-existing vertex intersections.
    // Each such pre-existing intersected vertex will be added to the "hot vertex" list, but at the same time will cause any intersected edge that it
    // belongs on to be discarded, as we cannot have 2 intersections on the same edge (or vertices on it).
    const hot_vertices = unique(addEdge_draw_intersected_vertices);

    if (addEdge_draw_intersected_edges.length + addEdge_draw_intersected_vertices.length < 2) {
        display_message_to_user(LOG.WARN, "Please try again moving mouse slower, and make sure you intersect the model edges.");
        wrap_up_drawing_edge_on_face();
    }

    // remove edges that contain hot vertices
    addEdge_draw_intersected_edges = addEdge_draw_intersected_edges
        .filter(eid => !hot_vertices.some(vid => uil_is_vertex_of_edge(vid, eid)))

    await withProgress('preparing intersections', (eid) => {
        // Consider each intersected edge
        const edge_intersection_origins = pipe(
            addEdge_draw_raycasting_origins,
            map(ray_origin => {
                const farb = v3.copy(addEdge_draw_raycasting_direction)
                    .multiplyScalar(-100)
                    .add(ray_origin);
                uid_raycaster.set(farb, addEdge_draw_raycasting_direction);
                const ray_intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

                return object_with_id_has_been_intersected(ray_intersects, eid)
                    ? v3.copy(uid_raycaster.ray.origin)
                    : undefined;
            }),
            filter(x => x !== undefined),
            x => Array.from(x),
        );

        const p = middle(edge_intersection_origins);
        // Remove edge from symmetry set to avoid generating opposite side point.
        remove_from_symmetry_set([eid], []);
        const new_vertex_id = uil_add_new_vertex(eid, p, uid_raycaster.ray.direction);
        if (new_vertex_id)
            hot_vertices.push(new_vertex_id);
        else
            display_message_to_user(LOG.WARN, 'Failed to add vertex to edge.');
    })(addEdge_draw_intersected_edges);

    await withProgress('adding edges', (fid) => {
        // Now consider all faces and any hot vertices on them.
        // Note that initially I thought we only needed to consider intersected faces,
        // but turns out we also want to consider faces that didn't show up as intersected yet had two
        // hot vertices on them. But not ALL such faces, only the ones where the two hot vertices are (almost)
        // along the direction of the raycasting ray. This is to cover for cases where we were raycasting almost
        // perpendicular to the normal of the face.
        if (mustNotDisplayFace(fid)) return;

        const face_hot_vertices = hot_vertices.filter(vid => uil_is_vertex_of_face(vid, fid));
        // we need 2 vertices to add an edge to this face
        if (face_hot_vertices.length != 2)
            return;

        // Replay the raycasting rays to hit the specific face
        const points = [];
        const normals = [];

        for (const ray_origin of addEdge_draw_raycasting_origins) {
            // Set up raycaster and find intersections
            const farb = v3.copy(addEdge_draw_raycasting_direction)
                .multiplyScalar(-100)
                .add(ray_origin);
            uid_raycaster.set(farb, addEdge_draw_raycasting_direction);
            const ray_intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
            // We want samples that hit the face, but not the edges around the face.
            const o = find_intersection_face_object_with_id(ray_intersects, fid);
            const edge_arr = find_all_intersection_edge_objects_relevant_to_face(ray_intersects, uid_faces.getItem(fid));
            if (o && edge_arr.length == 0) {
                if (points.length == 0 || v3.point_point_distance(o.point, points[points.length - 1]) > 0.000001) {
                    //add_small_ball(o.point.x, o.point.y, o.point.z, 255, 0, 0); // XXX
                    points.push(v3.copy(o.point));
                    normals.push(v3.copy(o.face.normal));
                }
            }
        }

        // See which end of the array of intersection points is closer to the one hot vertex, and which to the other.
        var v1;
        var v2;
        if (points.length >= 2 &&
            (v3.point_point_distance(uid_vertices.getItem(face_hot_vertices[0]).object3d.position, points[0]) <
                v3.point_point_distance(uid_vertices.getItem(face_hot_vertices[1]).object3d.position, points[0]))) {
            [v1, v2] = face_hot_vertices;
        } else {
            [v2, v1] = face_hot_vertices;
        }

        // Add the edge to the face
        const current_edge_id = add_edge_to_hit_unfixed_face(
            fid,
            v1,
            v2,
            points,
            normals,
        );

        if (!current_edge_id)
            display_message_to_user(LOG.WARN, "Failed to add edge to face.");

        add_vertices_to_hit_edges(
            addEdge_draw_raycasting_new_vertex_origins,
            current_edge_id,
            v2,
        );
    })(uid_faces.keys());

    wrap_up_drawing_edge_on_face();
}

/** add edge to intersected face.
 *
 * return the id of the current edge (or 0)
 */
export function add_edge_to_hit_unfixed_face(fid, v1, v2, points, normals) {
    if (uil_is_object_fixed(fid)) {
        display_message_to_user(LOG.WARN, "Cannot add edge on fixed face.");
        return 0;
    }
    const make_edge_linear = shift_was_pressed_during_adding_edge && face_is_flat(fid);
    const current_edge_id = add_edge_along_points(
        v1,
        v2,
        fid,
        points,
        normals,
        true,
        make_edge_linear
    );
    return current_edge_id;
}

/**
 * Now from the list of ray origins in addEdge_draw_raycasting_new_vertex_origins, see which ones hit an edge.
 * For any that do, add the vertex to the edge.
 * Do this one at a time.
 *
 * Note that as we add vertices, new edges are formed, so we need to re-scan the entire list each time.
 */
export const add_vertices_to_hit_edges = withSuppressedLogging(function add_vertices_to_hit_edges(vertex_origins, current_edge_id, v2) {
    for (const ray_origin of vertex_origins) {
        // Set up raycaster and find intersections
        const farb = v3.copy(addEdge_draw_raycasting_direction)
            .multiplyScalar(-100)
            .add(ray_origin);
        uid_raycaster.set(farb, addEdge_draw_raycasting_direction);
        const intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);

        // First check that this vertex oigin+ray hit the plane we're working on now
        // (which was fid but has been segmented by the new edge),
        // otherwise skip it.
        // Also set the ray origin at this point.
        const o1 = find_intersection_face_object_with_id(intersects, uid_api_add_edge_new_face1_id);
        const o2 = find_intersection_face_object_with_id(intersects, uid_api_add_edge_new_face2_id);

        if (o1) ray_origin.copy(o1.point);
        else if (o2) ray_origin.copy(o2.point);
        else continue;

        // Work on the edge identified by current_edge_id (the one we originally added to this face, or the one that resulted after
        // the addition of the latest vertex)
        const e = uid_edges.getItem(current_edge_id);
        // XXX Add vertex to edge, finding closest point on it first
        const wv_parameter = new WrappedValue();
        const p = uil_get_ray_edge_intersection(e.id, ray_origin, addEdge_draw_raycasting_direction, true, wv_parameter);
        const vid = uil_add_new_vertex(e.id, p, addEdge_draw_raycasting_direction);
        //console.log("Adding new vertex " + vid + " on edge " + e.id + "while working on face " + fid);
        if (!vid) {
            display_message_to_user(LOG.WARN, "Failed to add vertex to edge.");
            continue;
        }
        // Then move it to where the the vertex was originally indicated during the face click (i.e. at ray_origin)
        // XXX Note that ray_origin will be on the face with the precision given by the raycaster. We could instead
        //         find a point with much better precision by using the core to intersect the ray (ray_origin,    addEdge_draw_raycasting_direction)
        //         with the face with id fid - or rather the two new faces that replaced it.
        uil_move_vertex_to_coords( vid, ray_origin.x, ray_origin.y, ray_origin.z );
        // Then update current_edge_id with the one of the two new edges added as a result of the vertex addition that contains the 2nd hot vertex, v2.
        // Also smoothen the other edge (that will not get any new vertices afterwards)
        if (uil_get_vertices_of_edge(uid_api_add_vertex_new_edge1_id).indexOf(v2) != -1) {
            current_edge_id = uid_api_add_vertex_new_edge1_id;
            uil_smoothen_edges([uid_api_add_vertex_new_edge2_id], SOFT_STRENGTH);
            uil_smoothen_edges([uid_api_add_vertex_new_edge2_id], SOFT_STRENGTH);
        } else if (uil_get_vertices_of_edge(uid_api_add_vertex_new_edge2_id).indexOf(v2) != -1) {
            current_edge_id = uid_api_add_vertex_new_edge2_id;
            uil_smoothen_edges([uid_api_add_vertex_new_edge1_id], SOFT_STRENGTH);
            uil_smoothen_edges([uid_api_add_vertex_new_edge1_id], SOFT_STRENGTH);
        } else console_log_xml("Error identifying new current edge while drawing on face");
    }
});

export function wrap_up_drawing_edge_on_face() {
    // Reset linear draw state
    uid_linear_draw_direction = null;

    if (temporarily_hiding_mirror_side) {
        mirror_plane_base.copy(temporary_mirror_plane_base);
        mirror_plane_normal.copy(temporary_mirror_plane_normal);
        App.state.mirror_plane_selected = true;
        complete_setting_mirror_plane();
        temporarily_hiding_mirror_side = false;
    }

    App.state.hidden_edges_visible = hidden_edges_were_visible_before_drawing_edge;

    unsuppress_undo_tracking_if_ready(false);
    request_setting_button_states();
    remove_temporary_highlight_of_faces();
    animate();
}

export function abort_drawing_edge_on_face() {
    // Reset the state
    App.state.drawing_edge = false;
    App.state.carving = false;
    App.state.allow_rotate = true;

    // Remove points from scene
    clear_small_balls();
    uid_deselect_all_objects();

    // Decrease precision of raycaster
    uid_raycaster.params.Line.threshold *= 2;

    wrap_up_drawing_edge_on_face();
}

export function start_offset_face() {
    if ( App.state.offset_face_selecting_vertex || App.state.offset_face_dragging )
        return;

    if (uil_is_object_fixed(uid_selected_faces.firstKey())) {
        display_message_to_user(LOG.WARN, "Cannot offset a fixed face.")
        return;
    }

    // Check if at least one vertex around the face is visible
    var vs = vertices_of_face(uid_selected_faces.firstKey());
    var all_vertices_hidden = true;
    vs.forEach(function(vid){
        if (!mustNotDisplayVertex(vid))
            all_vertices_hidden = false;
    });
    if (all_vertices_hidden) {
        display_message_to_user(LOG.WARN, "You need at least one visible vertex on the face you are offsetting");
        return;
    }

    App.state.offset_face_selecting_vertex = true;
    offset_face_object = uid_selected_faces.firstValue();

    // Set offset face plane base and normal
    var center = new THREE.Vector3();
    var normal = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var dir = new THREE.Vector3();
    uil_get_face_geometry( offset_face_object.id, center, normal, wv_radius, dir, wv_flatness );
    offset_face_plane_base.copy(center);
    offset_face_plane_normal.copy(normal);

    // Deselect the face
    uid_toggle_select_object(offset_face_object.id, true);
    // Make it temporarily highlighted
    //offset_face_object.object3d.material.color.setRGB( color_face_working_r/256, color_face_working_g/256, color_face_working_b/256 );
    // Store original offset face color
    offset_face_color_r = offset_face_object.color_r;
    offset_face_color_g = offset_face_object.color_g;
    offset_face_color_b = offset_face_object.color_b;
    offset_face_object.color_r = color_face_working_r;
    offset_face_object.color_g = color_face_working_g;
    offset_face_object.color_b = color_face_working_b;
    set_default_face_color(offset_face_object.id);

    update_dom_text_values_and_coordinates();

    animate();
}

export function interrupt_offset_face() {
    if (!App.state.offset_face_dragging &&
        !App.state.offset_face_selecting_vertex)
        return;

    if (App.state.offset_face_dragging) {
        App.state.offset_face_dragging = false;
        uid_end_offset_face(offset_face_object.id, offset_face_vertex.id);
    }

    App.state.offset_face_selecting_vertex = false;
    App.state.offset_face_dragging = false;

    if (offset_face_object != null) {
        offset_face_object.color_r = offset_face_color_r;
        offset_face_object.color_g = offset_face_color_g;
        offset_face_object.color_b = offset_face_color_b;
        if (offset_face_object.object3d)
            //offset_face_object.object3d.material.color.setRGB( color_r_face/256, color_g_face/256, color_b_face/256 );
            set_default_face_color(offset_face_object.id);
    }

    if (offset_face_vertex != null) {
        offset_face_vertex.color_r = color_r_vertex;
        offset_face_vertex.color_g = color_g_vertex;
        offset_face_vertex.color_b = color_b_vertex;
        if (offset_face_vertex.object3d)
            //offset_face_vertex.object3d.material.color.setRGB( color_r_vertex/256, color_g_vertex/256, color_b_vertex/256 );
            set_default_vertex_color(offset_face_vertex.id);
    }

    offset_face_object = null;
    offset_face_vertex = null;
    offset_face_vertices = [];
    offset_point_coordinates = null;
    uid_deselect_all_objects();
    unsuppress_undo_tracking_if_ready(false);
}

export async function switch_view(direction, use_negative_direction) {
    if (App.state.currently_rotating_viewpoint)
        return;
    App.state.currently_rotating_viewpoint = true;

    viewing_down_orthogonal_plane = true;

    animate();

    viewdir = get_camera_look_vector(uid_camera);
    viewup = uid_camera.up;

    let camera_target_point = find_camera_target_point_for_switch_view(uid_camera);
    show_rotation_center(camera_target_point.x, camera_target_point.y, camera_target_point.z, false);

    let closest_direction = new THREE.Vector3();
    let closest_up = new THREE.Vector3();

    let set_axis_and_angle = false;
    if (camera_looking_down_orthogonal_axis(uid_camera)) {
        const local_epsilon = 1e-4;
        let angle = Math.PI;
        let axis = new THREE.Vector3();
        if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(1,0,0), local_epsilon) &&
            direction == "x" && use_negative_direction) {
            axis.set(0,0,1);
            closest_direction.set(-1,0,0);
            closest_up.set(0,0,1);
            set_axis_and_angle = true;
        }
        else if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(-1,0,0), local_epsilon) &&
            direction == "x" && !use_negative_direction) {
            axis.set(0,0,1);
            closest_direction.set(1,0,0);
            closest_up.set(0,0,1);
            set_axis_and_angle = true;
        }
        else if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(0,1,0), local_epsilon) &&
            direction == "y" && use_negative_direction) {
            axis.set(0,0,1);
            closest_direction.set(0,-1,0);
            closest_up.set(0,0,1);
            set_axis_and_angle = true;
        }
        else if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(0,-1,0), local_epsilon) &&
            direction == "y" && !use_negative_direction) {
            axis.set(0,0,1);
            closest_direction.set(0,1,0);
            closest_up.set(0,0,1);
            set_axis_and_angle = true;
        }
        else if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(0,0,1), local_epsilon) &&
        direction == "z" && use_negative_direction) {
            axis.set(0,1,0);
            closest_direction.set(0,0,-1);
            closest_up.set(0,1,0);
            set_axis_and_angle = true;
        }
        else if (v3.proximate(get_camera_look_vector(uid_camera), new THREE.Vector3(0,0,-1), local_epsilon) &&
        direction == "z" && !use_negative_direction) {
            axis.set(0,1,0);
            closest_direction.set(0,0,1);
            closest_up.set(0,1,0);
            set_axis_and_angle = true;
        }

        if (set_axis_and_angle) {
            await rotate_camera_by_axis_and_angle_in_steps(uid_camera, camera_target_point, axis, angle, 20);
            adjust_camera_up_and_update_projection_matrix(uid_camera);
        }
    }

    if (!set_axis_and_angle) {
        // The current camera look, up and side vectors
        let look_v = get_camera_look_vector(uid_camera);
        let up_v = new THREE.Vector3().copy(uid_camera.up).normalize();

        // Find the orthogonal direction that is closest to the current camera look direction
        let min_a = Infinity;
        const orthogonal_directions = [
            new THREE.Vector3(1,0,0),
            new THREE.Vector3(-1,0,0),
            new THREE.Vector3(0,1,0),
            new THREE.Vector3(0,-1,0),
            new THREE.Vector3(0,0,1),
            new THREE.Vector3(0,0,-1),
        ];

        const up_directions = [
            new THREE.Vector3(0,0,1),
            new THREE.Vector3(0,0,1),
            new THREE.Vector3(0,0,1),
            new THREE.Vector3(0,0,1),
            new THREE.Vector3(0,1,0),
            new THREE.Vector3(0,1,0),
        ];

        for (let i = 0; i < 6; i++) {
            if (look_v.angleTo(orthogonal_directions[i]) < min_a) {
                closest_direction.copy(orthogonal_directions[i]);
                closest_up.copy(up_directions[i]);
                min_a = look_v.angleTo(orthogonal_directions[i]);
            }
        }

        if (direction == "x") {
            closest_direction.copy(use_negative_direction ? orthogonal_directions[1] : orthogonal_directions[0]);
            closest_up.copy(up_directions[0]);
        }
        else if (direction == "y") {
            closest_direction.copy(use_negative_direction ? orthogonal_directions[3] : orthogonal_directions[2]);
            closest_up.copy(up_directions[2]);
        }
        else if (direction == "z") {
            closest_direction.copy(use_negative_direction ? orthogonal_directions[5] : orthogonal_directions[4]);
            closest_up.copy(up_directions[4]);
        }
        min_a = look_v.angleTo(closest_direction);

        let axis = new THREE.Vector3();
        // Find rotation to go from look vector to closest_direction
        if (min_a != 0) {
            axis.crossVectors(look_v, closest_direction).normalize();
            await rotate_camera_by_axis_and_angle_in_steps(uid_camera, camera_target_point, axis, min_a, 10);
        }
        adjust_camera_up_and_update_projection_matrix(uid_camera);
        //uid_camera.updateProjectionMatrix();
    }

    // Rotate around axis to adjust up vector.
    let up_a = uid_camera.up.angleTo(closest_up);
    if (up_a != 0) {
        let up_a_directedL = v3.cc_angle(uid_camera.up, closest_up, closest_direction);
        let up_a_directedR = v3.cc_angle(closest_up, uid_camera.up, closest_direction);
        if (up_a_directedL < up_a_directedR)
            await rotate_camera_by_axis_and_angle_in_steps(uid_camera, camera_target_point, closest_direction, up_a_directedL, 10);
        else
            await rotate_camera_by_axis_and_angle_in_steps(uid_camera, camera_target_point, closest_direction.multiplyScalar(-1), up_a_directedR, 10);
    }
    adjust_camera_up_and_update_projection_matrix(uid_camera);

    // Now rotate to correct face


    /*
    // Define a plane that is perpendicular to the camera look vector, and passes through the target point, and intersect it with the
    // camera look vector to find the point on it that is straight ahead of the camera. And the distance between this point and
    // the target point. Break them down in movement along the camera.up direction, and movement along the sideways direction.
    const camera_target_plane_center = plane_ray_intersect(rotation_center, look_v, uid_camera.position, look_v, 1.0);
    const center_target_vector = new THREE.Vector3().subVectors(rotation_center, camera_target_plane_center);
    let up_move = center_target_vector.dot(up_v);
    let side_move = center_target_vector.dot(side_v);
    let look_move = v3.point_point_distance(uid_camera.position, camera_target_plane_center);
    */

    /*
    // Where we want the camera to sit along the axis.
    const bb = uid_calculate_global_bounding_box(true, false, false);
    const position_along_axis = Math.max(bb.dimension * 2.0, 10);

    const { target_position, target_look_at, target_up, tmpv, intermediate } = (() => {
        switch (direction) {
            case 'x':
                if (use_negative_direction)
                    return {
                        target_position: new THREE.Vector3(position_along_axis, camera_target_point.y, camera_target_point.z),
                        target_look_at: new THREE.Vector3(0, camera_target_point.y, camera_target_point.z),
                        target_up: new THREE.Vector3(0, 0, 1),
                        tmpv: new THREE.Vector3(1, 0, 0),
                        intermediate: 'y',
                    };
                else
                    return {
                        target_position: new THREE.Vector3(position_along_axis * (-1), camera_target_point.y, camera_target_point.z),
                        target_look_at: new THREE.Vector3(0, camera_target_point.y, camera_target_point.z),
                        target_up: new THREE.Vector3(0, 0, 1),
                        tmpv: new THREE.Vector3(-1, 0, 0),
                        intermediate: 'y',
                    };

            case 'y':
                if (use_negative_direction)
                    return {
                        target_position: new THREE.Vector3(camera_target_point.x, position_along_axis, camera_target_point.z),
                        target_look_at: new THREE.Vector3(camera_target_point.x, 0, camera_target_point.z),
                        target_up: new THREE.Vector3(0, 0, 1),
                        tmpv: new THREE.Vector3(0, 1, 0),
                        intermediate: 'x',
                    };
                else
                    return {
                        target_position: new THREE.Vector3(camera_target_point.x, position_along_axis * (-1), camera_target_point.z),
                        target_look_at: new THREE.Vector3(camera_target_point.x, 0, camera_target_point.z),
                        target_up: new THREE.Vector3(0, 0, 1),
                        tmpv: new THREE.Vector3(0, -1, 0),
                        intermediate: 'x',
                    };

            case 'z':
                if (use_negative_direction)
                    return {
                        target_position: new THREE.Vector3(camera_target_point.x, camera_target_point.y, position_along_axis),
                        target_look_at: new THREE.Vector3(camera_target_point.x, camera_target_point.y, 0),
                        target_up: new THREE.Vector3(0, 1, 0),
                        tmpv: new THREE.Vector3(0, 0, 1),
                        intermediate: 'tmpxy',
                    };
                else
                    return {
                        target_position: new THREE.Vector3(camera_target_point.x, camera_target_point.y, position_along_axis * (-1)),
                        target_look_at: new THREE.Vector3(camera_target_point.x, camera_target_point.y, 0),
                        target_up: new THREE.Vector3(0, 1, 0),
                        tmpv: new THREE.Vector3(0, 0, -1),
                        intermediate: 'tmpxy',
                    };
            case 'tmpxy': // An intermediate step for rotating between z/-z
                return {
                    target_position: new THREE.Vector3(position_along_axis, camera_target_point.y, camera_target_point.z),
                    target_look_at: new THREE.Vector3(0, camera_target_point.y, camera_target_point.z),
                    target_up: new THREE.Vector3(0, 1, 0),
                    tmpv: new THREE.Vector3(0, 1, 0),
                    intermediate: 'y',
                };

        }
    })();

    // If we are are going to flip 180 degrees, use y view as an intermediate position for a better animation
    const tmpvd = new THREE.Vector3().copy(viewdir);
    if (tmpvd.dot(tmpv) >= 0.9) {
        switch (intermediate) {
            case "x":
                await switch_view("x", use_negative_direction);
                break;
            case "y":
                await switch_view("y", use_negative_direction);
                break;
            case "tmpxy":
                await switch_view("tmpxy", use_negative_direction);
                break;
        }
    }
    await move_camera_to(target_position, target_look_at, target_up);
    */

    if (App.state.grid_visible) {
        toggle_grid(direction);
        toggle_grid(direction);
    }

    adjust_camera_up_and_update_projection_matrix(uid_camera);
    update_dom_text_values_and_coordinates();
    animate();
    App.state.currently_rotating_viewpoint = false;
    hide_rotation_center();
}

export async function switch_default_view() {
    const bb = uid_calculate_global_bounding_box(true, false, false);

    if (!uid_camera)
        return;

    const target_look_at = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
    const target_position = new THREE.Vector3()
        .copy(target_look_at)
        .add(new THREE.Vector3(20,10,10));
    const target_up = new THREE.Vector3(0, 0, 1);
    const v = new THREE.Vector3()
        .subVectors(target_look_at, target_position)
        .normalize()
        .crossVectors(target_up, v)
        .normalize();
    const v1 = new THREE.Vector3()
        .copy(target_position)
        .normalize();
    target_up.crossVectors(v, v1);

    await move_camera_to(target_position, target_look_at, target_up);
    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();
    animate();

    await sleep(10);

    const bbdimension = uid_calculate_global_bounding_box(true, false, false).dimension;
    const n_edges = uid_edges.length + uid_reference_edges.length;
    const must_make_faster = n_edges > 1000;
    const direction = viewSize > bbdimension * 2 ? 1 : -1;
    const max_steps = must_make_faster ? 20 : 200;
    const time_step = 5;
    const speed_factor = must_make_faster ? 3.0 : 1.0;
    camera_zoom_step(direction, bbdimension, max_steps, time_step, speed_factor);
}

export async function rotate_one_step_right_or_left(is_right, angle=15) {
    if (App.state.currently_rotating_viewpoint)
        return;
    App.state.currently_rotating_viewpoint = true;

    var must_handle_rigid_move_manipulator = false;
    if (App.state.rigid_move_manipulator) {
        must_handle_rigid_move_manipulator = true;
        uid_hide_rigid_move_manipulator(true, 0, false);
    }

    var rot_angle = is_right ? angle * Math.PI/180.0 : angle * Math.PI/180.0 * (-1);
    uid_state.camera_target_point = find_camera_target_point_for_switch_view(uid_camera);
    show_rotation_center(uid_state.camera_target_point.x, uid_state.camera_target_point.y, uid_state.camera_target_point.z, false);

    await rotate_camera_by_axis_and_angle_in_steps(uid_camera, uid_state.camera_target_point, uid_camera.up, rot_angle, 10.0);

    if (viewing_down_orthogonal_plane) {
        // We're just leaving the orthogonal plane. Change grid color, if grid is visible.
        if (App.state.grid_visible) {
            toggle_grid(null, true);
            toggle_grid(null, true);
        }
        viewing_down_orthogonal_plane = false;
    }
    else if (camera_looking_down_orthogonal_axis(uid_camera)) {
        // We just entered viewing down an orthogonal plane
        viewing_down_orthogonal_plane = true;
        if (App.state.grid_visible) {
            toggle_grid(null);
            toggle_grid(null);
        }
    }
    if (must_handle_rigid_move_manipulator) {
        uid_show_rigid_move_manipulator(true, 0, false, false, false, false, false, false);
        must_handle_rigid_move_manipulator = false;
    }
    animate();
    App.state.currently_rotating_viewpoint = false;
    hide_rotation_center();
    adjust_camera_position_along_axis_and_depth(uid_camera);
}

export async function rotate_one_step_up_or_down(is_up, angle=15) {
    if (App.state.currently_rotating_viewpoint)
        return;
    App.state.currently_rotating_viewpoint = true;

    var must_handle_rigid_move_manipulator = false;
    if (App.state.rigid_move_manipulator) {
        must_handle_rigid_move_manipulator = true;
        uid_hide_rigid_move_manipulator(true, 0, false);
    }

    var rot_angle = is_up ? angle * Math.PI/180.0 * (-1) : angle * Math.PI/180.0;
    uid_state.camera_target_point = find_camera_target_point_for_switch_view(uid_camera);
    let axis = new THREE.Vector3().crossVectors(uid_camera.up, get_camera_look_vector(uid_camera));

    show_rotation_center(uid_state.camera_target_point.x, uid_state.camera_target_point.y, uid_state.camera_target_point.z, false);

    await rotate_camera_by_axis_and_angle_in_steps(uid_camera, uid_state.camera_target_point, axis, rot_angle, 10.0);

    if (viewing_down_orthogonal_plane) {
        // We're just leaving the orthogonal plane. Change grid color, if grid is visible.
        if (App.state.grid_visible) {
            toggle_grid(null, true);
            toggle_grid(null, true);
        }
        viewing_down_orthogonal_plane = false;
    }
    else if (camera_looking_down_orthogonal_axis(uid_camera)) {
        // We just entered viewing down an orthogonal plane
        viewing_down_orthogonal_plane = true;
        if (App.state.grid_visible) {
            toggle_grid(null);
            toggle_grid(null);
        }
    }
    if (must_handle_rigid_move_manipulator) {
        uid_show_rigid_move_manipulator(true, 0, false, false, false, false, false, false);
        must_handle_rigid_move_manipulator = false;
    }
    animate();
    App.state.currently_rotating_viewpoint = false;
    hide_rotation_center();
    adjust_camera_position_along_axis_and_depth(uid_camera);
}


export async function rotate_all_around_object() {
    animate();

    const pLocal = new THREE.Vector3(0, 0, -1);
    const pWorld = pLocal.applyMatrix4(uid_camera.matrixWorld);

    viewdir = pWorld.sub(uid_camera.position).normalize();
    viewup = uid_camera.up;

    // Find the "object center" where the camera is looking
    const bb = uid_calculate_global_bounding_box(true, false, false);

    {
        const target_position = new THREE.Vector3(10, bb.centery, bb.centerz);
        const target_look_at = new THREE.Vector3(0, bb.centery, bb.centerz);
        const target_up = new THREE.Vector3(0,0,1);
        const tmpv = new THREE.Vector3(1, 0, 0);
        const intermediate = "y";
        await move_camera_to(target_position, target_look_at, target_up);
    }

    {
        const target_position = new THREE.Vector3(bb.centerx, 10, bb.centerz);
        const target_look_at = new THREE.Vector3(bb.centerx, 0, bb.centerz);
        const target_up = new THREE.Vector3(0,0,1);
        const tmpv = new THREE.Vector3(0, 1, 0);
        const intermediate = "x";
        await move_camera_to(target_position, target_look_at, target_up);
    }

    {
        const target_position = new THREE.Vector3(bb.centerx, bb.centery, -10);
        const target_look_at = new THREE.Vector3(bb.centerx, bb.centery, 0);
        const target_up = new THREE.Vector3(1,0,0);
        const tmpv = new THREE.Vector3(0, 0, -1);
        const intermediate = "nx";
        await move_camera_to(target_position, target_look_at, target_up);
    }
}

export function move_to_viewport(v) {
    move_camera_to(v.position, v.look_target, v.up);

    // Set camera dimensions and adjust things that depend on it (lineprecision, vertex sizes)

    var dx = uid_camera.right - uid_camera.left;
    var dy = uid_camera.top - uid_camera.bottom;
    uid_camera.left = v.left;
    uid_camera.right = v.right;
    uid_camera.top = v.top;
    uid_camera.bottom = v.bottom;

    var oldViewSize = dx / aspectRatio;
    viewSize = (uid_camera.right - uid_camera.left) / aspectRatio;
    viewToWorld = viewSize / App.state.canvas.height;
    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();

    // Adjust line precision accordingly
    //uid_raycaster.linePrecision = 0.05;
    uid_raycaster.params.Line.threshold = viewSize/uid_raycaster_line_precision_factor;


    if (!App.state.currently_hiding_vertices)
        resize_all_vertices_for_zoom(oldViewSize, viewSize, 1);

    if (App.state.rigid_move_manipulator) {
        //resize_rigid_move_manipulator();
        uid_hide_rigid_move_manipulator(true, 0, false);
        uid_show_rigid_move_manipulator(true, 0, false, false, false, false, false, false);
    }

    if (!App.state.currently_hiding_vertices) {
        display_toggle_vertex_visibility(true, false, true);
    }
    hide_modal("storeRetrieveViewportModal");
    animate();
}

export async function view_all(do_oblique = false) {
    var bb = uid_calculate_global_bounding_box(true, false, false);

    if (!uid_camera)
        return;

    /* At Jeremy's request, we don't rotate the viewpoint, we just zoom to fit */
    var target_look_at = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
    var target_position = new THREE.Vector3();
    var target_up = new THREE.Vector3();

    if (do_oblique) {
        // Switch to an oblique view
        target_position.copy(target_look_at);
        var look_vector = new THREE.Vector3(-0.8,1,-0.5).normalize();
        target_position.addScaledVector(look_vector, Math.max(20, bb.dimension) * 2.0 * (-1) );
        target_up.set(0, 0, 1);
        var v = new THREE.Vector3();
        v.subVectors(target_look_at, target_position).normalize();
        v.crossVectors(target_up, v).normalize();
        var v1 = new THREE.Vector3();
        v1.copy(target_position).normalize();
        target_up.crossVectors(v, v1);
    }
    else {
        var look_vector = get_camera_look_vector(uid_camera).normalize();
        target_position.copy(target_look_at);
        target_position.addScaledVector(look_vector, Math.max(20, bb.dimension) * 2.0 * (-1) );
        target_up.copy(uid_camera.up);
    }

    await move_camera_to(target_position, target_look_at, target_up);

    uid_camera.near = 0;
    uid_camera.far = Math.max(80, bb.dimension * 4.0);

    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();
    animate();

    var bbdimension = uid_calculate_global_bounding_box(true, false, false).dimension;
    const n_edges = uid_edges.length + uid_reference_edges.length;
    const must_make_faster = n_edges > 1000;
    var direction = viewSize > bbdimension * 2 ? 1 : -1;
    const max_steps = must_make_faster ? 20 : 200;
    const time_step = 5;
    const speed_factor = must_make_faster ? 3.0 : 1.0;

    uid_state.camera_still_moving = true;

    var show_vertices_after = false;
    if (!App.state.currently_hiding_vertices) {
        show_vertices_after = true;
        display_toggle_vertex_visibility();
    }
    await camera_zoom_step(direction, bbdimension, max_steps, time_step, speed_factor);
    if (show_vertices_after)
        display_toggle_vertex_visibility();

    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();

    // Calculate bounding box. If camera is closer than 3 X (bounding box dimension), move it away along its view direction.
    // Note that at this point the camera is assumed to be looking at the bounding box center.
    if (uid_camera.position.length() < 3 * bb.dimension) {
        var v = get_camera_look_vector(uid_camera);
        var p = new THREE.Vector3(bb.centerx, bb.centery, bb.centerz);
        p.addScaledVector(v, (-1) * 3 * bb.dimension)
        uid_camera.position.x = p.x;
        uid_camera.position.y = p.y;
        uid_camera.position.z = p.z;
        adjust_camera_up_and_update_projection_matrix(uid_camera);
        //uid_camera.updateProjectionMatrix();
    }

    if (App.state.is_demo && App.state.aside)
        setTimeout(() => {
            move_camera_by_vector(uid_camera, new Vector3(0.35, 0.35, 0));
        }, 500);

    adjust_camera_position_along_axis_and_depth(uid_camera);
    animate();
}

export function camera_zoom_step(direction, bbdimension, max_steps, time_step, speed_factor = 1.0) {
    return new Promise(async (done) => {
        uid_state.camera_still_moving = true;
        let zoom_done = false;
        for (let step = 0; step < max_steps && !zoom_done; step++) {
            if (
                (direction === 1 && bbdimension * 2 < viewSize)
                || (direction === -1 && bbdimension * 1.5 > viewSize)
            ) {
                zoom(direction, null, speed_factor);
                await sleep(time_step);
            }
            else
                zoom_done = true;
        }
        uid_state.camera_still_moving = false;
        done(undefined);
    });
}

export function switch_mirror_plane_perpendicular_view() {
    // If it's parallel to an orthogonal plane, call switch_view

    var xaxis = new THREE.Vector3(1, 0, 0);
    var yaxis = new THREE.Vector3(0, 1, 0);
    var zaxis = new THREE.Vector3(0, 0, 1);
    const e = 0.001;
    if (v3.approximately_coaxial(mirror_plane_normal, xaxis, e))
        switch_view("x", must_view_down_negative_direction("x"));
    else if (v3.approximately_coaxial(mirror_plane_normal, yaxis, e))
        switch_view("y", must_view_down_negative_direction("y"));
    else if (v3.approximately_coaxial(mirror_plane_normal, zaxis, e))
        switch_view("z", must_view_down_negative_direction("z"));
    else {
        // Otherwise, find the face center and normal
        var center = new THREE.Vector3();
        center.copy(mirror_plane_base);
        var normal = new THREE.Vector3();
        normal.copy(mirror_plane_normal);
        var look_vector = get_camera_look_vector(uid_camera);
        var a = normal.angleTo(look_vector);
        if (a < Math.PI / 2.0)
            normal.multiplyScalar(-1);
        switch_perpendicular_view(center, normal);
    }
}

export function switch_symmetry_plane_perpendicular_view() {
    // If it's parallel to an orthogonal plane, call switch_view
    var xaxis = new THREE.Vector3(1, 0, 0);
    var yaxis = new THREE.Vector3(0, 1, 0);
    var zaxis = new THREE.Vector3(0, 0, 1);
    const e = 0.001;
    if (v3.approximately_coaxial(symmetry_plane_normal, xaxis, e))
        switch_view("x", must_view_down_negative_direction("x"));
    else if (v3.approximately_coaxial(symmetry_plane_normal, yaxis, e))
        switch_view("y", must_view_down_negative_direction("y"));
    else if (v3.approximately_coaxial(symmetry_plane_normal, zaxis, e))
        switch_view("z", must_view_down_negative_direction("z"));
    else {
        // Otherwise, find the face center and normal
        var center = new THREE.Vector3();
        center.copy(symmetry_plane_base);
        var normal = new THREE.Vector3();
        normal.copy(symmetry_plane_normal);
        var look_vector = get_camera_look_vector(uid_camera);
        var a = normal.angleTo(look_vector);
        if (a < Math.PI / 2.0)
            normal.multiplyScalar(-1);
        switch_perpendicular_view(center, normal);
    }
}

export function switch_face_view() {
    // Find the face center and normal
    var center = new THREE.Vector3();
    var normal = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var dir = new THREE.Vector3();

    var fid = uid_selected_faces.firstKey();

    uil_get_face_geometry(fid, center, normal, wv_radius, dir, wv_flatness);
    switch_perpendicular_view(center, normal);
}

export function switch_perpendicular_view(center, normal) {
    animate();

    var pLocal = new THREE.Vector3(0, 0, -1);
    var pWorld = pLocal.applyMatrix4(uid_camera.matrixWorld);

    viewdir = pWorld.sub(uid_camera.position).normalize();
    viewup = uid_camera.up;
    pLocal = null;

    var look_vector = get_camera_look_vector(uid_camera);

    var camera_position = new THREE.Vector3();

    // Find an "up vector" by selecting the x, y, or z axis that is closest to the plane (i.e. closest to PI/2 angle with the normal) and
    // projecting it on the normal.

    var vx = new THREE.Vector3(1, 0, 0);
    var vy = new THREE.Vector3(0, 1, 0);
    var vz = new THREE.Vector3(0, 0, 1);
    var ax = v3.angle(normal, vx);
    var ay = v3.angle(normal, vy);
    var az = v3.angle(normal, vz);

    var upv = new THREE.Vector3();

    if (Math.abs(ax - Math.PI / 2) < Math.abs(ay - Math.PI / 2)) {
        if (Math.abs(ax - Math.PI / 2) < Math.abs(az - Math.PI / 2)) {
            upv.copy(vx);
        } else {
            upv.copy(vz);
        }
    } else {
        if (Math.abs(ay - Math.PI / 2) < Math.abs(az - Math.PI / 2)) {
            upv.copy(vy);
        } else {
            upv.copy(vz);
        }
    }
    upv = project_vector_on_plane(upv, center, normal);

    var n = new THREE.Vector3();
    n.copy(normal);
    n.multiplyScalar(10);
    camera_position.copy(center);
    camera_position.add(n);

    var target_position = new THREE.Vector3(camera_position.x, camera_position.y, camera_position.z);
    var target_look_at = new THREE.Vector3(center.x, center.y, center.z);
    var target_up = new THREE.Vector3(upv.x, upv.y, upv.z);
    move_camera_to(target_position, target_look_at, target_up);

    adjust_camera_up_and_update_projection_matrix(uid_camera);
    //uid_camera.updateProjectionMatrix();
    animate();
}

export async function loadTutorials(showTutorials) {
    hideTutorialsModal();

    if (showTutorials) {
        handle_tutorial_state();
        global_default_model_data = new DefaultModelData();
        await loadGivenModelString({
            selectedModel: 'cube',
            file_name: 'cube ' + new Date().toLocaleString(),
            x: 1,
            y: 1,
            z: 1,
            height: 1,
            diameter: 1,
        });
    }
    else
        App.state.modal.selectModelModal = true;

    animate();
}

export function unhighlight_currently_highlighted_object() {
    if (uid_highlighted_object_o3d) {
        uid_unhighlight_object(uid_highlighted_object_o3d);
        uid_highlighted_object_o3d = null;
    }
}
